import {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useCallback,
  useMemo,
  Fragment,
} from "react";
import "../css/PagesCommonStyle.css";
import {
  faCheckCircle,
  faMagnifyingGlass,
  faPrint,
  faShieldHalved,
  faIdCard,
  faStethoscope,
  faSackDollar,
  faFileInvoice,
  faListCheck,
  faCircleCheck,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileExcel,
  faFilePdf,
  faTrashCan,
  faEye,
  faEyeSlash,
  faFileLines
} from "@fortawesome/free-regular-svg-icons";
import { useSelector, useDispatch } from "react-redux";
import {
  faTriangleExclamation,
  faStar,
  faCircleInfo,
  faXmark,
  faPlus,
  faMinus,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import {
  APIConstants,
  IntelliBillConstants,
  CommonAPIConstants,
  FilesAPIConstants,
  formatDOSDate,
  LabBillingConstants,

} from "../utilities/Constants";
import Draggable from "react-draggable";
import { Link } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, subDays } from "date-fns";
import ProviderDropdown from "../components/CascadingDropdown";
import { exportExcel } from "../utilities/exportToExcel";
import PrintColumnModal from "../components/PrintColumnModal";

// DOS comes back as a datetime from the prod API ("08/01/2026 00:00:00") and as a plain
// date from dev. Format it in one place so the grid, the DOS column search and the Excel
// export can never disagree about what a row's DOS says. Falls back to the raw value so
// an unparseable date is still shown rather than silently blanked.
const getDosText = (item) =>
  formatDOSDate(item.Appointment_Date) || String(item.Appointment_Date ?? "");

const COLUMN_WIDTHS = {
  "#": "40px",
  AppLocationName: "85px",
  DOS: "70px",
  PatientName: "120px",
  ProviderDisplay: "100px",
  DOB: "70px",
  SelectedInsuranceName: "120px",
  ProvderName: "200px",
  CPT: "130px",
  Mod1: "120px",
  Mod2: "120px",
  Mod3: "120px",
  ICDCode: "135px",
  ICDCode2: "135px",
  ICDCode3: "135px",
  ICDCode4: "135px",
  Unit: "100px",
  Unitcharge: "130px",
  TotalCharge: "130px",
  CopayAmount: "110px",
  ClaimId: "120px",
  ClearingHouse: "160px",
  ServiceLocationCode: "170px",
  Select: "75px",
};

const readableInkOn = (background) => {
  if (!background) return null;
  const raw = String(background).trim();

  let r;
  let g;
  let b;

  const hex = raw.replace(/^#/, "");
  const expanded =
    hex.length === 3
      ? hex.split("").map((ch) => ch + ch).join("")
      : hex;

  if (/^[0-9a-f]{6}$/i.test(expanded)) {
    [r, g, b] = [0, 2, 4].map((i) => parseInt(expanded.slice(i, i + 2), 16));
  } else {
    const rgb = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (!rgb) return null;
    [r, g, b] = [rgb[1], rgb[2], rgb[3]].map(Number);
    if ([r, g, b].some((channel) => channel > 255)) return null;
  }

  const toLinear = (channel) => {
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  const luminance =
    0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);

  return luminance > 0.179 ? "#0f172a" : "#ffffff";
};

const locationSwatch = (background) => {
  const color = readableInkOn(background);
  return color ? { backgroundColor: background, color } : undefined;
};

const BILLING_QUIPS = [
  "🧪 Pulling requisitions off the bench…",
  "🔬 Reading every order code twice…",
  "🧾 Matching panels to their CPT codes…",
  "🩺 Lining up referring providers and NPIs…",
  "🧬 Decoding ICDs before the payer does…",
  "🏥 Sweet-talking the insurance overlords…",
  "🔍 Scrubbing requisitions squeaky clean…",
  "💰 Making sure every panel gets paid…",
  "☕ Brewing up some lab billing magic…",
];

const labBillingStyles = `
*,
*::before,
*::after {
  box-sizing: border-box;
}

/* html/body horizontal clamping now lives in styles/healthiva/_base.scss and
   deliberately lets the document scroll sideways. The copy that used to sit
   here re-applied the old clamp and, being injected into the body, outranked
   it. Deleted rather than re-synced. */

body {
  margin: 0;
  background: #dce3ea;
}

img, svg, video, canvas {
  max-width: 100%;
}

@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
    scroll-behavior: auto !important;
  }
}
@media print {
  body {
    background: #fff;
  }
  .hv-toolbar, .hv-pagi, .hv-tabbar {
    display: none !important;
  }
}
.hv-container, .hv-drawer, .hv-modal, .hv-toasts {
  font-family: "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  font-size: 14px;
  line-height: 1.45;
  color: #0f172a;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}
.hv-container button,
.hv-container input,
.hv-container select,
.hv-container textarea,
.hv-container optgroup, .hv-drawer button,
.hv-drawer input,
.hv-drawer select,
.hv-drawer textarea,
.hv-drawer optgroup, .hv-modal button,
.hv-modal input,
.hv-modal select,
.hv-modal textarea,
.hv-modal optgroup, .hv-toasts button,
.hv-toasts input,
.hv-toasts select,
.hv-toasts textarea,
.hv-toasts optgroup {
  font-family: "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
}
.hv-container table, .hv-container th, .hv-container td,
.hv-container .hv-num,
.hv-container [data-numeric], .hv-drawer table, .hv-drawer th, .hv-drawer td,
.hv-drawer .hv-num,
.hv-drawer [data-numeric], .hv-modal table, .hv-modal th, .hv-modal td,
.hv-modal .hv-num,
.hv-modal [data-numeric], .hv-toasts table, .hv-toasts th, .hv-toasts td,
.hv-toasts .hv-num,
.hv-toasts [data-numeric] {
  font-variant-numeric: tabular-nums;
}
.hv-container a, .hv-drawer a, .hv-modal a, .hv-toasts a {
  color: #1a9b8c;
  text-decoration: none;
}
.hv-container a:hover, .hv-drawer a:hover, .hv-modal a:hover, .hv-toasts a:hover {
  text-decoration: underline;
}
.hv-container h1, .hv-container h2, .hv-container h3, .hv-container h4, .hv-container h5, .hv-container h6, .hv-drawer h1, .hv-drawer h2, .hv-drawer h3, .hv-drawer h4, .hv-drawer h5, .hv-drawer h6, .hv-modal h1, .hv-modal h2, .hv-modal h3, .hv-modal h4, .hv-modal h5, .hv-modal h6, .hv-toasts h1, .hv-toasts h2, .hv-toasts h3, .hv-toasts h4, .hv-toasts h5, .hv-toasts h6 {
  margin: 0;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.hv-container p, .hv-drawer p, .hv-modal p, .hv-toasts p {
  margin: 0;
}
.hv-container button, .hv-drawer button, .hv-modal button, .hv-toasts button {
  font: inherit;
}
.hv-container :focus-visible, .hv-drawer :focus-visible, .hv-modal :focus-visible, .hv-toasts :focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
}
.hv-container ::selection, .hv-drawer ::selection, .hv-modal ::selection, .hv-toasts ::selection {
  background: rgba(26, 155, 140, 0.22);
}
.hv-container *, .hv-drawer *, .hv-modal *, .hv-toasts * {
  scrollbar-color: #cfd8e3 transparent;
}
.hv-container *::-webkit-scrollbar, .hv-drawer *::-webkit-scrollbar, .hv-modal *::-webkit-scrollbar, .hv-toasts *::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}
.hv-container *::-webkit-scrollbar-thumb, .hv-drawer *::-webkit-scrollbar-thumb, .hv-modal *::-webkit-scrollbar-thumb, .hv-toasts *::-webkit-scrollbar-thumb {
  background: #cfd8e3;
  border-radius: 999px;
  border: 3px solid transparent;
  background-clip: content-box;
}
.hv-container *::-webkit-scrollbar-thumb:hover, .hv-drawer *::-webkit-scrollbar-thumb:hover, .hv-modal *::-webkit-scrollbar-thumb:hover, .hv-toasts *::-webkit-scrollbar-thumb:hover {
  background: #a8b3c2;
  background-clip: content-box;
}
.hv-container *::-webkit-scrollbar-track, .hv-drawer *::-webkit-scrollbar-track, .hv-modal *::-webkit-scrollbar-track, .hv-toasts *::-webkit-scrollbar-track {
  background: transparent;
}

body.hv-typo,
body.hv-typo button,
body.hv-typo input,
body.hv-typo select,
body.hv-typo textarea {
  font-family: "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
}

.hv-container {
  width: 100%;
  max-width: none;
  min-width: 0;
  padding-inline: clamp(12px, 1.5vw, 28px);
  padding-block: clamp(12px, 1.4vw, 22px) 60px;
}

.hv-page {
  animation: hv-fade-in 0.22s cubic-bezier(0.4, 0, 0.2, 1) both;
}
@keyframes hv-fade-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

.hv-pagehead {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.hv-pagehead__title {
  font: 800 clamp(20px, 2vw, 28px)/1.15 "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #1a9b8c;
  letter-spacing: -0.02em;
}
.hv-pagehead__sub {
  margin-top: 6px;
  font-size: 13px;
  color: #64748b;
}
.hv-pagehead__actions {
  margin-left: auto;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
@media (max-width: 640px) {
  .hv-pagehead__actions {
    margin-left: 0;
    width: 100%;
  }
}

.hv-grid {
  display: grid;
  gap: 12px;
  align-items: stretch;
  margin-bottom: 18px;
  grid-template-columns: repeat(auto-fit, minmax(min(210px, 100%), 1fr));
}
.hv-grid > * {
  min-width: 0;
}
.hv-grid--sm {
  grid-template-columns: repeat(auto-fit, minmax(min(160px, 100%), 1fr));
}
.hv-grid--wide {
  grid-template-columns: repeat(auto-fit, minmax(min(380px, 100%), 1fr));
  gap: 14px;
}
.hv-grid--half {
  grid-template-columns: repeat(auto-fit, minmax(min(480px, 100%), 1fr));
  gap: 14px;
}
.hv-grid--flush {
  margin-bottom: 0;
}

.hv-analytics {
  display: grid;
  gap: 14px;
  margin-top: 16px;
  grid-template-columns: minmax(0, 2fr) repeat(2, minmax(280px, 1fr));
}
.hv-analytics > * {
  min-width: 0;
}
@media (max-width: 1280px) {
  .hv-analytics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .hv-analytics > .hv-analytics__hero {
    grid-column: 1/-1;
  }
}
@media (max-width: 768px) {
  .hv-analytics {
    grid-template-columns: minmax(0, 1fr);
  }
  .hv-analytics > .hv-analytics__hero {
    grid-column: auto;
  }
}

.hv-split {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
}
.hv-split > * {
  min-width: 0;
}
.hv-split--sidebar {
  grid-template-columns: minmax(0, 1fr) minmax(260px, 320px);
}
@media (max-width: 1024px) {
  .hv-split {
    grid-template-columns: minmax(0, 1fr);
  }
}

.hv-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  min-width: 0;
}
.hv-row--tight {
  gap: 6px;
}
.hv-row--between {
  justify-content: space-between;
}
.hv-row--top {
  align-items: flex-start;
}

.hv-stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.hv-stack--tight {
  gap: 6px;
}

.hv-spacer {
  flex: 1 1 auto;
}

.hv-divider {
  height: 1px;
  background: #edf1f6;
  border: 0;
  margin: 14px 0;
}

.hv-btn {
  box-sizing: border-box;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #cfd8e3;
  border-radius: 10px;
  background: #f8fafc;
  color: #0f172a;
  font: 400 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  transition: border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
  border-color: #1a9b8c;
}
.hv-btn:disabled, .hv-btn[aria-disabled=true] {
  opacity: 0.5;
  cursor: not-allowed;
}
.hv-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 16px;
  border-color: #1a9b8c;
  background: #1a9b8c;
  color: #fff;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.12s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1), transform 0.12s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-btn:hover:not(:disabled) {
  background: #0e6f5c;
  border-color: #0e6f5c;
}
.hv-btn:active:not(:disabled) {
  transform: translateY(1px);
}
.hv-btn svg {
  width: 15px;
  height: 15px;
}
.hv-btn--ghost {
  background: #ffffff;
  border-color: #cfd8e3;
  color: #334155;
}
.hv-btn--ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #1a9b8c;
  color: #1a9b8c;
}
.hv-btn--soft {
  background: #e6f4f2;
  border-color: #b9e2dc;
  color: #0e6f5c;
}
.hv-btn--soft:hover:not(:disabled) {
  background: #f0faf8;
  border-color: #1a9b8c;
}
.hv-btn--quiet {
  background: transparent;
  border-color: transparent;
  color: #64748b;
}
.hv-btn--quiet:hover:not(:disabled) {
  background: #f8fafc;
  color: #0f172a;
}
.hv-btn--danger {
  background: #dc2626;
  border-color: #dc2626;
}
.hv-btn--danger:hover:not(:disabled) {
  background: #991b1b;
  border-color: #991b1b;
}
.hv-btn--sm {
  height: 30px;
  padding: 0 12px;
  font-size: 12px;
}
.hv-btn--lg {
  height: 42px;
  padding: 0 22px;
  font-size: 14px;
}
.hv-btn--block {
  width: 100%;
}
.hv-btn.is-loading {
  pointer-events: none;
  position: relative;
  color: transparent;
}
.hv-btn.is-loading::after {
  content: "";
  position: absolute;
  inset: 0;
  margin: auto;
  width: 15px;
  height: 15px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: hv-spin 0.6s linear infinite;
}

@keyframes hv-spin {
  to {
    transform: rotate(360deg);
  }
}
.hv-icon-btn {
  box-sizing: border-box;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #cfd8e3;
  border-radius: 10px;
  background: #f8fafc;
  color: #0f172a;
  font: 400 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  transition: border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-icon-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
  border-color: #1a9b8c;
}
.hv-icon-btn:disabled, .hv-icon-btn[aria-disabled=true] {
  opacity: 0.5;
  cursor: not-allowed;
}
.hv-icon-btn {
  width: 36px;
  padding: 0;
  display: inline-grid;
  place-items: center;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
}
.hv-icon-btn:hover:not(:disabled) {
  color: #1a9b8c;
  border-color: #1a9b8c;
  background: #e6f4f2;
}
.hv-icon-btn svg {
  width: 15px;
  height: 15px;
}
.hv-icon-btn--sm {
  width: 30px;
  height: 30px;
}
.hv-icon-btn--bare {
  border-color: transparent;
  background: transparent;
}

.hv-btn-group {
  display: inline-flex;
}
.hv-btn-group .hv-btn {
  border-radius: 0;
}
.hv-btn-group .hv-btn:first-child {
  border-radius: 10px 0 0 10px;
}
.hv-btn-group .hv-btn:last-child {
  border-radius: 0 10px 10px 0;
}
.hv-btn-group .hv-btn + .hv-btn {
  margin-left: -1px;
}

.hv-input,
.hv-date,
.hv-textarea {
  box-sizing: border-box;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #cfd8e3;
  border-radius: 10px;
  background: #f8fafc;
  color: #0f172a;
  font: 400 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  transition: border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-input:focus-visible,
.hv-date:focus-visible,
.hv-textarea:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
  border-color: #1a9b8c;
}
.hv-input:disabled, .hv-input[aria-disabled=true],
.hv-date:disabled,
.hv-date[aria-disabled=true],
.hv-textarea:disabled,
.hv-textarea[aria-disabled=true] {
  opacity: 0.5;
  cursor: not-allowed;
}

.hv-input {
  width: 100%;
}
.hv-input::placeholder {
  color: #a8b3c2;
}
.hv-input--sm {
  height: 30px;
  font-size: 12px;
}

.hv-textarea {
  height: auto;
  min-height: 84px;
  padding: 10px;
  line-height: 1.5;
  resize: vertical;
}

.hv-date {
  min-width: 132px;
}

.hv-select {
  box-sizing: border-box;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #cfd8e3;
  border-radius: 10px;
  background: #f8fafc;
  color: #0f172a;
  font: 400 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  transition: border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-select:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
  border-color: #1a9b8c;
}
.hv-select:disabled, .hv-select[aria-disabled=true] {
  opacity: 0.5;
  cursor: not-allowed;
}
.hv-select {
  appearance: none;
  padding-right: 28px;
  cursor: pointer;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'><path d='M6 9l6 6 6-6'/></svg>");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 14px;
}
.hv-select--sm {
  height: 30px;
  font-size: 12px;
  padding: 0 24px 0 8px;
}

.hv-check {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #334155;
  white-space: nowrap;
  cursor: pointer;
  user-select: none;
}
.hv-check input {
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: #1a9b8c;
  cursor: pointer;
}
.hv-check--stack {
  align-items: flex-start;
  white-space: normal;
}

.hv-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #334155;
}
.hv-toggle input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}
.hv-toggle__track {
  position: relative;
  width: 38px;
  height: 21px;
  flex: 0 0 38px;
  border-radius: 999px;
  background: #cfd8e3;
  transition: background 0.18s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-toggle__track::after {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
  transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1);
}
input:checked + .hv-toggle__track {
  background: #1a9b8c;
}
input:checked + .hv-toggle__track::after {
  transform: translateX(17px);
}
input:focus-visible + .hv-toggle__track {
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
}

.hv-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.hv-field__label {
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 4px;
}
.hv-field__req {
  color: #dc2626;
}
.hv-field__hint {
  font-size: 11px;
  color: #a8b3c2;
}
.hv-field__error {
  font-size: 11px;
  font-weight: 600;
  color: #dc2626;
}
.hv-field.is-invalid .hv-input, .hv-field.is-invalid .hv-select, .hv-field.is-invalid .hv-date, .hv-field.is-invalid .hv-textarea {
  border-color: #dc2626;
  background: #fff6f6;
}
.hv-field--inline {
  flex-direction: row;
  align-items: center;
  gap: 8px;
}
.hv-field--inline .hv-field__label {
  white-space: nowrap;
}

.hv-search {
  position: relative;
  flex: 1 1 260px;
  min-width: 180px;
  max-width: 640px;
}
.hv-search input {
  box-sizing: border-box;
  height: 36px;
  padding: 0 10px;
  border: 1px solid #cfd8e3;
  border-radius: 10px;
  background: #f8fafc;
  color: #0f172a;
  font: 400 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  transition: border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-search input:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
  border-color: #1a9b8c;
}
.hv-search input:disabled, .hv-search input[aria-disabled=true] {
  opacity: 0.5;
  cursor: not-allowed;
}
.hv-search input {
  width: 100%;
  padding-left: 34px;
  background-color: #ffffff;
}
.hv-search input::placeholder {
  color: #a8b3c2;
}
.hv-search::before {
  content: "";
  position: absolute;
  left: 11px;
  top: 50%;
  width: 15px;
  height: 15px;
  transform: translateY(-50%);
  background: #a8b3c2;
  pointer-events: none;
  transition: background 0.12s cubic-bezier(0.4, 0, 0.2, 1);
  -webkit-mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><path d='M416 208c0 45.9-14.9 88.3-40 122.7l126.6 126.7c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.1-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0s208 93.1 208 208M208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288'/></svg>") center/contain no-repeat;
  mask: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'><path d='M416 208c0 45.9-14.9 88.3-40 122.7l126.6 126.7c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.1-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0s208 93.1 208 208M208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288'/></svg>") center/contain no-repeat;
}
.hv-search:focus-within::before {
  background: #1a9b8c;
}
.hv-search--sm input {
  height: 30px;
  font-size: 12px;
}
.hv-search--full {
  max-width: none;
}

.hv-toolbar {
  background: #ffffff;
  border: 1px solid #d8dee8;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 35, 58, 0.07), 0 4px 14px rgba(15, 35, 58, 0.05);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 14px;
  margin-bottom: 16px;
}
.hv-toolbar__group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.hv-toolbar__group label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
  white-space: nowrap;
}
.hv-toolbar__label {
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
}
@media (max-width: 640px) {
  .hv-toolbar {
    flex-direction: column;
    align-items: stretch;
    position: static;
  }
  .hv-toolbar > *,
  .hv-toolbar .hv-btn,
  .hv-toolbar .hv-select,
  .hv-toolbar .hv-date,
  .hv-toolbar .hv-input {
    width: 100%;
  }
  .hv-toolbar .hv-search {
    max-width: none;
    flex: 1 1 auto;
  }
  .hv-toolbar__group {
    flex-direction: column;
    align-items: stretch;
  }
  .hv-toolbar__group label {
    width: 100%;
  }
  .hv-toolbar__group .hv-date {
    width: 100%;
  }
}

.hv-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.hv-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #b9e2dc;
  border-radius: 999px;
  background: #e6f4f2;
  color: #0e6f5c;
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
}
.hv-chip__x {
  border: 0;
  background: none;
  padding: 0;
  line-height: 1;
  cursor: pointer;
  color: #0e6f5c;
  opacity: 0.6;
}
.hv-chip__x:hover {
  opacity: 1;
}

.hv-panel {
  background: #ffffff;
  border: 1px solid #d8dee8;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 35, 58, 0.07), 0 4px 14px rgba(15, 35, 58, 0.05);
  /* "hidden" cut the table off at the card edge, so nothing overflowed upward
     and the document never grew a scrollbar. The card is sized to the table
     instead, and does not clip. */
  overflow: visible;
  /* min-width, not width. As a flex item of the page column the card's used
     width kept resolving to the container, so "width: max-content" never
     stretched it and the rows ran out onto the page background. A max-content
     FLOOR always wins over the used width: the card is at least as wide as the
     table, and still fills the viewport when the table is narrower. */
  width: 100%;
  min-width: max-content;
}
.hv-panel__head {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border-bottom: 1px solid #edf1f6;
}
.hv-panel__heading {
  margin: 0;
  font: 700 14px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #0f172a;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  gap: 8px;
}
.hv-panel__heading::before {
  content: "";
  flex: 0 0 3px;
  width: 3px;
  height: 15px;
  border-radius: 2px;
  background: #1a9b8c;
}
.hv-panel__hint {
  margin-left: auto;
  font-size: 12px;
  color: #64748b;
}
.hv-panel__actions {
  margin-left: auto;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.hv-panel__body {
  padding: 16px;
}
.hv-panel__foot {
  padding: 12px 16px;
  border-top: 1px solid #edf1f6;
  background: #f8fafc;
}
.hv-panel--flush .hv-panel__body {
  padding: 0;
}
.hv-panel--flush > .hv-table-scroll {
  border-radius: 0 0 16px 16px;
}
.hv-panel--quiet {
  box-shadow: none;
}
@media (max-width: 640px) {
  .hv-panel__head {
    padding: 12px;
  }
  .hv-panel__body {
    padding: 12px;
  }
  .hv-panel__hint, .hv-panel__actions {
    margin-left: 0;
    width: 100%;
  }
}

.hv-stat {
  background: #ffffff;
  border: 1px solid #d8dee8;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 35, 58, 0.07), 0 4px 14px rgba(15, 35, 58, 0.05);
  transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.18s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-stat:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(15, 35, 58, 0.14);
}
.hv-stat {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  height: 100%;
  min-width: 0;
  position: relative;
  overflow: hidden;
}
.hv-stat__icon {
  flex: 0 0 38px;
  width: 38px;
  height: 38px;
  display: grid;
  place-items: center;
  border-radius: 8px;
  background: #e6f4f2;
  color: #1a9b8c;
}
.hv-stat__icon svg {
  width: 17.1px;
  height: 17.1px;
}
.hv-stat__body {
  min-width: 0;
}
.hv-stat__value {
  font: 800 clamp(22px, 2.1vw, 30px)/1 "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  letter-spacing: -0.02em;
  color: #0f172a;
}
.hv-stat__label {
  margin-top: 6px;
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hv-stat__delta {
  margin-top: 4px;
  font: 700 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
}
.hv-stat__delta.is-up {
  color: #15803d;
}
.hv-stat__delta.is-down {
  color: #d97706;
}
.hv-stat--none .hv-stat__value {
  color: #0f172a;
}
.hv-stat--none .hv-stat__icon {
  background: #e6f4f2;
  color: #1a9b8c;
}
.hv-stat--brand .hv-stat__value {
  color: #1a9b8c;
}
.hv-stat--brand .hv-stat__icon {
  background: #e6f4f2;
  color: #1a9b8c;
}
.hv-stat--info .hv-stat__value {
  color: #12568a;
}
.hv-stat--info .hv-stat__icon {
  background: #dbeafe;
  color: #12568a;
}
.hv-stat--paid .hv-stat__value {
  color: #15803d;
}
.hv-stat--paid .hv-stat__icon {
  background: #dcfce7;
  color: #15803d;
}
.hv-stat--denied .hv-stat__value {
  color: #dc2626;
}
.hv-stat--denied .hv-stat__icon {
  background: #fee2e2;
  color: #dc2626;
}
.hv-stat--pending .hv-stat__value {
  color: #d97706;
}
.hv-stat--pending .hv-stat__icon {
  background: #fef3c7;
  color: #d97706;
}
.hv-stat--muted .hv-stat__value {
  color: #0f172a;
}
.hv-stat--muted .hv-stat__icon {
  background: #eef2f7;
  color: #64748b;
}
.hv-stat--action {
  cursor: pointer;
}
.hv-stat--action:focus-visible {
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
}
.hv-stat.is-active {
  border-color: #1a9b8c;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
}
@media (max-width: 640px) {
  .hv-stat {
    padding: 14px;
    gap: 10px;
  }
}

.hv-bucket {
  background: #ffffff;
  border: 1px solid #d8dee8;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 35, 58, 0.07), 0 4px 14px rgba(15, 35, 58, 0.05);
  transition: transform 0.18s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.18s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-bucket:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(15, 35, 58, 0.14);
}
.hv-bucket {
  padding: 14px;
  text-align: center;
  cursor: pointer;
  min-width: 0;
}
.hv-bucket__count {
  font: 800 22px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  line-height: 1;
}
.hv-bucket__label {
  margin-top: 6px;
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
}
.hv-bucket__bar {
  height: 4px;
  border-radius: 999px;
  margin-top: 10px;
}
.hv-bucket--1 .hv-bucket__count {
  color: #15803d;
}
.hv-bucket--1 .hv-bucket__bar {
  background: #15803d;
}
.hv-bucket--1.is-active {
  border-color: #86efac;
  background: #dcfce7;
}
.hv-bucket--2 .hv-bucket__count {
  color: #d97706;
}
.hv-bucket--2 .hv-bucket__bar {
  background: #d97706;
}
.hv-bucket--2.is-active {
  border-color: #fcd34d;
  background: #fef3c7;
}
.hv-bucket--3 .hv-bucket__count {
  color: #ea580c;
}
.hv-bucket--3 .hv-bucket__bar {
  background: #ea580c;
}
.hv-bucket--3.is-active {
  border-color: #fdba74;
  background: #ffedd5;
}
.hv-bucket--4 .hv-bucket__count {
  color: #dc2626;
}
.hv-bucket--4 .hv-bucket__bar {
  background: #dc2626;
}
.hv-bucket--4.is-active {
  border-color: #fca5a5;
  background: #fee2e2;
}
.hv-bucket--5 .hv-bucket__count {
  color: #991b1b;
}
.hv-bucket--5 .hv-bucket__bar {
  background: #991b1b;
}
.hv-bucket--5.is-active {
  border-color: #f87171;
  background: #fecaca;
}

.hv-kv {
  display: grid;
  grid-template-columns: minmax(96px, 34%) minmax(0, 1fr);
  gap: 8px 12px;
  font-size: 13px;
}
.hv-kv__k {
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
}
.hv-kv__v {
  color: #0f172a;
  font-weight: 600;
  min-width: 0;
}
@media (max-width: 640px) {
  .hv-kv {
    grid-template-columns: minmax(0, 1fr);
    gap: 4px 0;
  }
}

.hv-tabbar {
  display: flex;
  gap: 2px;
  border-bottom: 2px solid #d8dee8;
  margin-bottom: 16px;
  overflow-x: auto;
  overscroll-behavior-inline: contain;
  scrollbar-width: none;
}
.hv-tabbar::-webkit-scrollbar {
  display: none;
}
.hv-tabbar__tab {
  appearance: none;
  border: 0;
  background: none;
  cursor: pointer;
  padding: 10px 16px;
  margin-bottom: -2px;
  white-space: nowrap;
  font: 600 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
  border-bottom: 2px solid transparent;
  transition: color 0.12s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-tabbar__tab:hover {
  color: #0f172a;
}
.hv-tabbar__tab.is-active {
  color: #1a9b8c;
  font-weight: 700;
  border-bottom-color: #1a9b8c;
}
.hv-tabbar__count {
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  background: #edf1f6;
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
}
.hv-tabbar__tab.is-active .hv-tabbar__count {
  background: #e6f4f2;
  color: #0e6f5c;
}
.hv-tabbar--bar {
  align-items: center;
  gap: 10px;
  margin-bottom: 0;
  padding: 0 14px;
  flex-wrap: nowrap;
  font-size: 12px;
  color: #64748b;
}
.hv-tabbar--bar .hv-tabbar__tab {
  padding: 10px 12px;
}
.hv-tabbar--bar .hv-pagi {
  padding: 6px 0;
}
@media (max-width: 768px) {
  .hv-tabbar--bar {
    flex-wrap: wrap;
  }
  .hv-tabbar--bar .hv-pagi {
    margin-left: auto;
  }
}

.hv-seg {
  display: inline-flex;
  padding: 3px;
  border: 1px solid #cfd8e3;
  border-radius: 10px;
  background: #f8fafc;
}
.hv-seg__btn {
  border: 0;
  background: none;
  cursor: pointer;
  padding: 5px 12px;
  border-radius: 8px;
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
  white-space: nowrap;
  transition: background 0.12s cubic-bezier(0.4, 0, 0.2, 1), color 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-seg__btn:hover {
  color: #0f172a;
}
.hv-seg__btn.is-active {
  background: #ffffff;
  color: #1a9b8c;
  box-shadow: 0 1px 2px rgba(15, 35, 58, 0.12);
}

.hv-tablebar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 16px;
  border-bottom: 1px solid #edf1f6;
  font-size: 12px;
  color: #64748b;
}

.hv-pagi {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}
.hv-pagi__rows {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.hv-pagi select {
  box-sizing: border-box;
  height: 30px;
  padding: 0 10px;
  border: 1px solid #cfd8e3;
  border-radius: 10px;
  background: #f8fafc;
  color: #0f172a;
  font: 400 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  transition: border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-pagi select:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
  border-color: #1a9b8c;
}
.hv-pagi select:disabled, .hv-pagi select[aria-disabled=true] {
  opacity: 0.5;
  cursor: not-allowed;
}
.hv-pagi select {
  padding: 0 24px 0 8px;
  font-size: 12px;
  background-color: #ffffff;
}
.hv-pagi__btn {
  min-width: 30px;
  height: 30px;
  padding: 0 8px;
  border: 1px solid #cfd8e3;
  border-radius: 8px;
  background: #ffffff;
  color: #334155;
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  cursor: pointer;
  transition: all 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-pagi__btn:hover:not(:disabled) {
  border-color: #1a9b8c;
  color: #1a9b8c;
}
.hv-pagi__btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.hv-pagi__btn.is-active {
  background: #1a9b8c;
  border-color: #1a9b8c;
  color: #fff;
}
.hv-pagi__gap {
  padding: 0 2px;
  color: #a8b3c2;
}
@media (max-width: 640px) {
  .hv-pagi {
    margin-left: 0;
    width: 100%;
    justify-content: center;
  }
}

.hv-crumbs {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 10px;
  font-size: 12px;
  color: #64748b;
}
.hv-crumbs__link {
  color: #64748b;
}
.hv-crumbs__link:hover {
  color: #1a9b8c;
}
.hv-crumbs__sep {
  color: #a8b3c2;
}
.hv-crumbs__current {
  color: #0f172a;
  font-weight: 600;
}

.hv-topbar-progress {
  position: fixed;
  inset: 0 0 auto 0;
  height: 2px;
  z-index: 1200;
  background: #1a9b8c;
  transform-origin: 0 50%;
  animation: hv-progress 1.4s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes hv-progress {
  0% {
    transform: scaleX(0);
    opacity: 1;
  }
  70% {
    transform: scaleX(0.85);
    opacity: 1;
  }
  100% {
    transform: scaleX(1);
    opacity: 0;
  }
}
.hv-shell {
  display: flex;
  align-items: stretch;
}
.hv-shell__main {
  flex: 1 1 auto;
}

.hv-sidebar {
  flex: 0 0 240px;
  width: 240px;
  min-width: 0;
  overflow: hidden;
  background: #ffffff;
  border-right: 1px solid #d8dee8;
  transition: flex-basis 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-sidebar__inner {
  width: 240px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.hv-sidebar__link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 0;
  border-radius: 10px;
  background: #1a9b8c;
  color: #fff;
  font: 600 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s cubic-bezier(0.4, 0, 0.2, 1), transform 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-sidebar__link:hover {
  background: #0e6f5c;
  text-decoration: none;
}
.hv-sidebar__link:active {
  transform: translateY(1px);
}
.hv-sidebar__link.is-active {
  background: #d97706;
  font-weight: 700;
}
.hv-sidebar__icon {
  flex: 0 0 18px;
  width: 18px;
  display: grid;
  place-items: center;
}
.hv-sidebar__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hv-sidebar__group {
  margin-top: 14px;
  padding: 0 14px 6px;
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 11px;
}

.hv-sidebar__link.is-active:hover {
  background: #b45f05;
}

.hv-shell:not(.is-open) .hv-sidebar {
  flex-basis: 0;
  width: 0;
  border-right-color: transparent;
}

.hv-shell--rail:not(.is-open) .hv-sidebar {
  flex-basis: 56px;
  width: 56px;
  border-right-color: #d8dee8;
}
.hv-shell--rail:not(.is-open) .hv-sidebar .hv-sidebar__inner {
  width: 56px;
  padding: 12px 6px;
}
.hv-shell--rail:not(.is-open) .hv-sidebar .hv-sidebar__label,
.hv-shell--rail:not(.is-open) .hv-sidebar .hv-sidebar__group {
  display: none;
}
.hv-shell--rail:not(.is-open) .hv-sidebar .hv-sidebar__link {
  justify-content: center;
  padding: 12px 0;
}

.hv-sidebar-scrim {
  display: none;
}

@media (max-width: 1024px) {
  .hv-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 1000;
    width: min(240px, 82vw);
    flex: none;
    box-shadow: 0 24px 60px rgba(15, 35, 58, 0.22);
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }
  .hv-sidebar__inner {
    width: 100%;
  }
  .hv-shell.is-open .hv-sidebar {
    transform: none;
  }
  .hv-shell:not(.is-open) .hv-sidebar,
  .hv-shell--rail:not(.is-open) .hv-sidebar {
    flex-basis: auto;
    width: min(240px, 82vw);
  }
  .hv-shell.is-open .hv-sidebar-scrim {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 900;
    background: rgba(15, 35, 58, 0.42);
    animation: hv-scrim-in 0.18s cubic-bezier(0.4, 0, 0.2, 1) both;
  }
}
@media (prefers-reduced-motion: reduce) {
  .hv-sidebar {
    transition: none;
  }
}
/* No overflow of its own any more: the horizontal scroll belongs to the
   document, so the browser draws one bar at the bottom of the window instead
   of a private one under the last row. */
.hv-table-scroll {
  width: 100%;
  /* explicit, not merely omitted: the design system's base rule sets
     overflow-x: auto and max-width: 100%, and dropping the declarations here
     would just fall through to those. */
  max-width: none;
  overflow-x: visible;
  position: relative;
}
.hv-table-scroll--fade::after {
  content: "";
  position: sticky;
  right: 0;
  top: 0;
  display: block;
  width: 28px;
  height: 100%;
  pointer-events: none;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0), #ffffff);
}

.hv-table {
  width: 100%;
  min-width: max-content;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.hv-table thead th {
  position: sticky;
  top: 0;
  z-index: 10;
  padding: 12px 14px;
  text-align: left;
  white-space: nowrap;
  background: #f8fafc;
  border-bottom: 1px solid #d8dee8;
  font: 700 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #64748b;
}
.hv-table thead th.is-sortable {
  cursor: pointer;
  user-select: none;
}
.hv-table thead th.is-sortable:hover {
  color: #1a9b8c;
}
.hv-table__sort {
  margin-left: 4px;
  font-size: 9px;
  color: #a8b3c2;
}
.is-sorted-asc .hv-table__sort, .is-sorted-desc .hv-table__sort {
  color: #1a9b8c;
}
.hv-table__searchrow th {
  position: sticky;
  top: 34px;
  z-index: 10;
  padding: 6px 10px 8px;
  background: #ffffff;
  border-bottom: 1px solid #d8dee8;
}
.hv-table__searchrow th input {
  box-sizing: border-box;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #cfd8e3;
  border-radius: 10px;
  background: #f8fafc;
  color: #0f172a;
  font: 400 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  transition: border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-table__searchrow th input:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
  border-color: #1a9b8c;
}
.hv-table__searchrow th input:disabled, .hv-table__searchrow th input[aria-disabled=true] {
  opacity: 0.5;
  cursor: not-allowed;
}
.hv-table__searchrow th input {
  width: 100%;
  min-width: 80px;
  font-size: 11px;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  background: #f8fafc;
}
.hv-table__searchrow th input::placeholder {
  color: #a8b3c2;
}
.hv-table tbody td {
  padding: 12px 14px;
  border-bottom: 1px solid #edf1f6;
  color: #334155;
  white-space: nowrap;
  vertical-align: middle;
}
.hv-table tbody tr {
  transition: background 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-table tbody tr:hover td {
  background: #f0faf8;
}
.hv-table tbody tr:last-child td {
  border-bottom: 0;
}
.hv-table tbody tr.is-selected td {
  background: #e6f4f2;
}
.hv-table tbody tr.is-clickable {
  cursor: pointer;
}
.hv-table .hv-num-col, .hv-table__num {
  text-align: right;
}
.hv-table .hv-td-strong {
  font-weight: 600;
  color: #0f172a;
}
.hv-table .hv-td-muted {
  color: #a8b3c2;
}
.hv-table .hv-td-wrap {
  white-space: normal;
  min-width: 160px;
}
.hv-table .hv-td-nowrap {
  white-space: nowrap;
}
.hv-table .hv-td-link {
  font-weight: 700;
  color: #1a9b8c;
  cursor: pointer;
  display: inline;
  padding: 0;
  border-bottom: 0;
  text-decoration: none;
}
.hv-table .hv-td-link:hover {
  text-decoration: underline;
}
.hv-table .hv-td-link:visited {
  color: #1a9b8c;
}
.hv-table .hv-td-link.is-zero {
  color: #a8b3c2;
  font-weight: 400;
  cursor: default;
  text-decoration: none;
}
.hv-table .hv-td-link.is-zero:hover, .hv-table .hv-td-link.is-zero:visited {
  text-decoration: none;
  color: #a8b3c2;
}
.hv-table tfoot td,
.hv-table .hv-tr-total td {
  padding: 12px 14px;
  white-space: nowrap;
  vertical-align: middle;
  background: #e6f4f2;
  border-top: 2px solid #b9e2dc;
  border-bottom: 0;
  font-weight: 800;
  color: #0f172a;
}
.hv-table--compact {
  font-size: 12px;
}
.hv-table--compact thead th,
.hv-table--compact tbody td {
  padding: 8px 12px;
}
.hv-table--striped tbody tr:nth-child(even) td {
  background: #fbfdfe;
}

.hv-bulkbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 16px;
  background: #e6f4f2;
  border-bottom: 1px solid #b9e2dc;
  font-size: 12px;
  font-weight: 600;
  color: #0e6f5c;
}

.hv-defaults {
  background: #ffffff;
  border: 1px solid #d8dee8;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 35, 58, 0.07), 0 4px 14px rgba(15, 35, 58, 0.05);
  margin-bottom: 14px;
}
.hv-defaults__head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
  border: 0;
  background: none;
  width: 100%;
  text-align: left;
}
.hv-defaults__head:hover {
  background: #f8fafc;
}
.hv-defaults__title {
  margin: 0;
  font: 700 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.hv-defaults__summary {
  margin-left: auto;
  font-size: 12px;
  color: #64748b;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hv-defaults__caret {
  flex: 0 0 12px;
  color: #64748b;
  transition: transform 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-defaults.is-open .hv-defaults__caret {
  transform: rotate(180deg);
}
.hv-defaults__body {
  display: none;
  padding: 0 16px 16px;
  border-top: 1px solid #edf1f6;
  padding-top: 14px;
}
.hv-defaults.is-open .hv-defaults__body {
  display: block;
}
.hv-defaults__grid {
  display: grid;
  gap: 12px 16px;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
}
.hv-defaults__grid > * {
  min-width: 0;
}
.hv-defaults__grid--tight {
  grid-template-columns: repeat(auto-fit, minmax(min(190px, 100%), 1fr));
}
.hv-defaults__grid .hv-field {
  align-content: start;
}
.hv-defaults__flag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #fcd34d;
  background: #fef3c7;
  color: #d97706;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
  margin-left: 8px;
}

.hv-req::after {
  content: "*";
  margin-left: 2px;
  color: #dc2626;
  font-weight: 700;
}

.hv-worksheet thead th {
  background: #f8fafc;
  color: #64748b;
  border-bottom: 1px solid #d8dee8;
}
.hv-worksheet tbody td {
  padding: 8px 12px;
  vertical-align: middle;
}
.hv-worksheet.hv-table tbody tr:hover td {
  background: #f0faf8;
}
.hv-worksheet .hv-cellinput, .hv-worksheet .hv-cellselect {
  width: 100%;
  min-width: 64px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  font: 400 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
  transition: border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1), background 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-worksheet .hv-cellinput:hover, .hv-worksheet .hv-cellselect:hover {
  border-color: #cfd8e3;
  background: #ffffff;
}
.hv-worksheet .hv-cellinput:focus, .hv-worksheet .hv-cellselect:focus {
  outline: none;
  border-color: #1a9b8c;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.35);
}
.hv-worksheet .hv-cellinput[readonly], .hv-worksheet [readonly].hv-cellselect {
  color: #64748b;
  pointer-events: none;
}
.hv-worksheet .hv-cellinput--num {
  text-align: right;
}
.hv-worksheet .hv-cellinput--money {
  text-align: right;
}
.hv-worksheet .hv-cellselect {
  appearance: none;
  padding-right: 20px;
  cursor: pointer;
}
.hv-worksheet .hv-cellpair {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.hv-worksheet .hv-cellpair input[type=checkbox] {
  flex: 0 0 14px;
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: #1a9b8c;
  opacity: 0.45;
  transition: opacity 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-worksheet .hv-cellpair:hover input[type=checkbox],
.hv-worksheet .hv-cellpair input[type=checkbox]:checked {
  opacity: 1;
}
.hv-worksheet tr.is-invalid td:first-child {
  box-shadow: inset 3px 0 0 #dc2626;
}
.hv-worksheet .hv-errrow td {
  padding: 6px 12px 10px;
  background: #fffafa;
  border-bottom: 1px solid #edf1f6;
}
.hv-worksheet .hv-errlist {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 12px;
  font-size: 11px;
  color: #991b1b;
}
.hv-worksheet .hv-errlist span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.hv-worksheet .hv-errlist span::before {
  content: "";
  flex: 0 0 4px;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #dc2626;
}
.hv-worksheet .hv-errcount {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #fecaca;
  background: #fee2e2;
  color: #dc2626;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
  padding: 1px 6px;
  font-size: 10px;
}

.hv-worksheet--pinned .hv-pin {
  position: sticky;
  z-index: 3;
  background: #ffffff;
}
.hv-worksheet--pinned thead .hv-pin {
  z-index: 12;
  background: #f8fafc;
}
.hv-worksheet--pinned thead .hv-table__searchrow .hv-pin {
  background: #ffffff;
}
.hv-worksheet--pinned .hv-pin--1 {
  left: 0;
}
.hv-worksheet--pinned .hv-pin--2 {
  left: 44px;
}
.hv-worksheet--pinned .hv-pin--3 {
  left: 100px;
}
.hv-worksheet--pinned .hv-pin--4 {
  left: 250px;
}
.hv-worksheet--pinned .hv-pin--5 {
  left: 370px;
  box-shadow: 6px 0 8px -6px rgba(15, 35, 58, 0.18);
}
.hv-worksheet--pinned tbody tr:hover .hv-pin {
  background: #f0faf8;
}
.hv-worksheet--pinned tbody tr.is-selected .hv-pin {
  background: #e6f4f2;
}
.hv-worksheet--pinned tfoot .hv-pin,
.hv-worksheet--pinned .hv-tr-total .hv-pin {
  background: #e6f4f2;
}
.hv-worksheet--pinned .hv-errrow td {
  background: #fffafa;
}
.hv-worksheet--pinned .hv-errrow .hv-errlist {
  position: sticky;
  left: 12px;
  width: max-content;
  max-width: calc(100vw - 120px);
}
@media (max-width: 1024px) {
  .hv-worksheet--pinned .hv-pin--3, .hv-worksheet--pinned .hv-pin--4 {
    position: static;
    box-shadow: none;
  }
  .hv-worksheet--pinned .hv-pin--5 {
    left: 100px;
  }
}

.hv-worksheet__groups th {
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 6px 12px;
  background: #fbfdfe;
  border-bottom: 2px solid #d8dee8;
  font: 800 10px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  text-align: left;
  white-space: nowrap;
}
.hv-worksheet__groups th + th {
  border-left: 1px solid #d8dee8;
}
.hv-worksheet__groups th.is-id + th.is-id {
  border-left: 0;
}

.hv-worksheet--pinned thead .hv-worksheet__groups th.is-id {
  z-index: 13;
  color: #0e6f5c;
  background: #e6f4f2;
}

.hv-worksheet--grouped thead .hv-worksheet__groups th {
  top: 0;
}
.hv-worksheet--grouped thead tr:nth-child(2) th {
  top: 25px;
}
.hv-worksheet--grouped thead .hv-table__searchrow th {
  top: 60px;
}

.hv-actionbar {
  position: sticky;
  bottom: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  --hv-ab-pr: 16px;
  padding: 12px var(--hv-ab-pr) 12px 16px;
  margin-top: 14px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  border: 1px solid #d8dee8;
  border-radius: 16px;
  box-shadow: 0 8px 28px rgba(15, 35, 58, 0.14);
}
.hv-actionbar__count {
  font-size: 12px;
  color: #64748b;
}
.hv-actionbar__count strong {
  color: #0f172a;
  font-weight: 700;
}
.hv-actionbar__spacer {
  flex: 1 1 auto;
}
.hv-actionbar--fab {
  --hv-ab-pr: 88px;
}
@media (max-width: 640px) {
  .hv-actionbar {
    flex-direction: column;
    align-items: stretch;
    --hv-ab-pr: 16px;
    padding-bottom: 68px;
  }
  .hv-actionbar .hv-btn {
    width: 100%;
  }
}

.hv-exports {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding-left: 10px;
  margin-left: 6px;
  border-left: 1px solid #d8dee8;
}

.hv-table-scroll--viewport {
  max-height: calc(100vh - 320px);
  min-height: 240px;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-width: auto;
  scrollbar-color: #cfd8e3 #f8fafc;
}
.hv-table-scroll--viewport::-webkit-scrollbar {
  width: 12px;
  height: 12px;
}
.hv-table-scroll--viewport::-webkit-scrollbar-track {
  background: #f8fafc;
}
.hv-table-scroll--viewport::-webkit-scrollbar-thumb {
  background: #cfd8e3;
  border: 2px solid #f8fafc;
  border-radius: 999px;
  background-clip: content-box;
}
.hv-table-scroll--viewport::-webkit-scrollbar-thumb:hover {
  background: #a8b3c2;
  background-clip: content-box;
}
.hv-table-scroll--viewport::-webkit-scrollbar-corner {
  background: #f8fafc;
}
.hv-table-scroll--viewport tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 4;
}
.hv-table-scroll--viewport tfoot td.hv-pin {
  z-index: 11;
}
@media (max-width: 768px) {
  .hv-table-scroll--viewport {
    max-height: calc(100vh - 260px);
  }
}

.hv-panel__scrollhint {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font: 600 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
  white-space: nowrap;
}

.hv-page--dense {
  padding-block: 10px 14px;
}
.hv-page--dense .hv-pagehead {
  margin-bottom: 10px;
}
.hv-page--dense .hv-pagehead__title {
  font-size: clamp(18px, 1.6vw, 23px);
}
.hv-page--dense .hv-pagehead__sub {
  margin-top: 2px;
  font-size: 12px;
}
.hv-page--dense .hv-toolbar {
  gap: 8px;
  padding: 8px 12px;
  margin-bottom: 10px;
}
.hv-page--dense .hv-defaults {
  margin-bottom: 10px;
}
.hv-page--dense .hv-defaults__head {
  padding: 8px 14px;
}
.hv-page--dense .hv-defaults__body {
  padding: 12px 14px 14px;
}
.hv-page--dense .hv-defaults__grid {
  gap: 10px 14px;
}
.hv-page--dense .hv-panel__head {
  padding: 10px 14px;
}
.hv-page--dense .hv-worksheet tbody td {
  padding: 6px 12px;
}
.hv-page--dense .hv-worksheet thead th {
  padding: 8px 12px;
}
.hv-page--dense .hv-worksheet .hv-table__searchrow th {
  padding: 4px 10px 6px;
}
.hv-page--dense .hv-worksheet .hv-errrow td {
  padding: 4px 12px 6px;
}
.hv-page--dense .hv-actionbar {
  margin-top: 10px;
  padding: 10px var(--hv-ab-pr) 10px 14px;
}

/* .hv-page--app lives in styles/healthiva/_worksheet.scss and is loaded globally
   via theme.scss. The copy that used to sit here was a stale snapshot: it still
   had "> .hv-panel { flex: 1 1 auto }", the version the design system replaced
   with "0 1 auto" precisely because "1 1 auto" stretched the card past its last
   row and pushed the horizontal scrollbar below the fold. Because this style
   block is injected into the body it outranked the fixed rule in theme.scss, so
   the stale copy won. Deleted rather than re-synced: one source of truth. */

.hv-page--dense .hv-toolbar .hv-search {
  flex: 1 1 180px;
  min-width: 160px;
  max-width: 300px;
}
.hv-page--dense .hv-toolbar .hv-input {
  max-width: 190px;
}

.hv-worksheet .hv-col-select {
  text-align: center;
}
.hv-worksheet .hv-col-select input[type=checkbox] {
  margin: 0;
  accent-color: #1a9b8c;
}
.hv-worksheet .hv-errrow td {
  text-align: left;
  width: auto;
}

.hv-worksheet {
  width: 100%;
  min-width: 0;

}
.hv-worksheet th, .hv-worksheet td {
  overflow: hidden;
  text-overflow: ellipsis;
}
.hv-worksheet .hv-errrow td {
  overflow: visible;
}
.hv-worksheet .hv-table__searchrow th {
  text-overflow: clip;
}
.hv-worksheet .hv-table__searchrow th input {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}
.hv-worksheet .hv-fill {
  width: auto;
  min-width: 0;
  padding: 0;
  border-bottom: 1px solid #edf1f6;
  background: inherit;
}
.hv-worksheet thead .hv-fill {
  background: #f8fafc;
  border-bottom: 1px solid #d8dee8;
}
.hv-worksheet thead .hv-table__searchrow .hv-fill {
  background: #ffffff;
}
.hv-worksheet .hv-cellpair {
  justify-content: space-between;
  gap: 8px;
}
.hv-worksheet .hv-cellpair > :first-child {
  flex: 1 1 auto;
  min-width: 0;
}
.hv-worksheet .hv-cellcheck {
  display: flex;
  justify-content: flex-end;
}
.hv-worksheet .hv-cellcheck input[type=checkbox] {
  margin: 0;
  accent-color: #1a9b8c;
  opacity: 0.45;
}
.hv-worksheet .hv-cellcheck:hover input, .hv-worksheet .hv-cellcheck input:checked {
  opacity: 1;
}
.hv-worksheet .hv-cellflags {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
  flex: 0 0 auto;
}

.hv-tablebar__actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding-left: 10px;
  margin-left: 6px;
  border-left: 1px solid #d8dee8;
}

.hv-grpband th {
  position: sticky;
  top: 0;
  z-index: 5;
  padding: 6px 12px;
  background: #fbfdfe;
  border-bottom: 1px solid #d8dee8;
  font: 800 10px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  text-align: left;
  white-space: nowrap;
}
.hv-grpband th.is-frozen {
  z-index: 13;
  background: #e6f4f2;
  color: #0e6f5c;
}
.hv-grpband th.is-alt {
  background: #f2f6fb;
  border-left: 1px solid #d8dee8;
}

.hv-worksheet--pinned thead .hv-grpband th.is-frozen {
  position: sticky;
}

.hv-tabbar__issues {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #fecaca;
  background: #fee2e2;
  color: #dc2626;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
  margin-left: 6px;
  padding: 1px 6px;
  font-size: 10px;
}

.hv-tabbar__ok {
  margin-left: 6px;
  color: #15803d;
  font-size: 12px;
}

.hv-th-chk {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.hv-th-chk input[type=checkbox] {
  flex: 0 0 auto;
  margin: 0;
  accent-color: #1a9b8c;
}
.hv-th-chk > span {
  min-width: 0;
}
.hv-t-center > .hv-th-chk {
  justify-content: center;
}

.hv-th-sub {
  display: block;
  margin-top: 1px;
  font-size: 9px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: none;
  color: #a8b3c2;
}

.hv-gridwrap {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.hv-gridwrap::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  bottom: 12px;
  width: 36px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.18s cubic-bezier(0.4, 0, 0.2, 1);
  background: linear-gradient(90deg, rgba(255, 255, 255, 0), #ffffff);
}
.hv-gridwrap.has-more::after {
  opacity: 1;
}

.hv-sum {
  display: flex;
  align-items: baseline;
  gap: 6px;
  min-width: 0;
}
.hv-sum__main {
  font-weight: 600;
  color: #0f172a;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hv-sum__more {
  font-size: 11px;
  color: #64748b;
  white-space: nowrap;
}

.hv-expander {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: 1px solid #cfd8e3;
  border-radius: 8px;
  background: #ffffff;
  color: #64748b;
  cursor: pointer;
  font-size: 11px;
  line-height: 1;
  transition: transform 0.12s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.12s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-expander:hover {
  border-color: #1a9b8c;
  color: #1a9b8c;
}
.hv-expander.is-open {
  transform: rotate(90deg);
  border-color: #1a9b8c;
  color: #1a9b8c;
}

.hv-detailrow > td {
  padding: 0;
  background: #f8fafc;
  border-bottom: 1px solid #d8dee8;
}

.hv-detail {
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr));
  padding: 14px 16px;
}
.hv-detail__sec {
  min-width: 0;
}
.hv-detail__title {
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-bottom: 6px;
  margin-bottom: 8px;
  border-bottom: 1px solid #edf1f6;
}
.hv-detail__actions {
  grid-column: 1/-1;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  padding-top: 10px;
  border-top: 1px solid #edf1f6;
}

.hv-chart {
  width: 100%;
  height: clamp(180px, 19vw, 260px);
}
.hv-chart svg {
  width: 100%;
  height: 100%;
  overflow: visible;
}
.hv-chart__grid line {
  stroke: #edf1f6;
  stroke-width: 1;
  shape-rendering: crispEdges;
}
.hv-chart__axis {
  font: 400 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  fill: #64748b;
}
.hv-chart__line {
  fill: none;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.hv-chart__dot {
  r: 3.2;
  stroke: #fff;
  stroke-width: 1.5;
}
.hv-chart__area {
  opacity: 0.1;
}
.hv-chart--tall {
  height: clamp(240px, 26vw, 340px);
}
.hv-chart--short {
  height: clamp(120px, 12vw, 170px);
}

.hv-legend {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 10px;
  font: 600 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
}
.hv-legend__item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.hv-legend__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 8px;
}
.hv-legend__dot--1 {
  background: #1a9b8c;
}
.hv-legend__dot--2 {
  background: #15803d;
}
.hv-legend__dot--3 {
  background: #d97706;
}
.hv-legend__dot--4 {
  background: #dc2626;
}
.hv-legend__dot--5 {
  background: #12568a;
}
.hv-legend__dot--6 {
  background: #7c3aed;
}

.hv-series-1 {
  stroke: #1a9b8c;
  fill: #1a9b8c;
}

.hv-series-2 {
  stroke: #15803d;
  fill: #15803d;
}

.hv-series-3 {
  stroke: #d97706;
  fill: #d97706;
}

.hv-series-4 {
  stroke: #dc2626;
  fill: #dc2626;
}

.hv-series-5 {
  stroke: #12568a;
  fill: #12568a;
}

.hv-series-6 {
  stroke: #7c3aed;
  fill: #7c3aed;
}

.hv-bars {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.hv-bars__item {
  min-width: 0;
}
.hv-bars__top {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 6px;
  font-size: 13px;
}
.hv-bars__code {
  font-weight: 800;
  color: #0f172a;
  white-space: nowrap;
}
.hv-bars__count {
  margin-left: auto;
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
  white-space: nowrap;
}
.hv-bars__track {
  height: 7px;
  border-radius: 999px;
  background: #edf1f6;
  overflow: hidden;
}
.hv-bars__fill {
  height: 100%;
  border-radius: 999px;
  background: #1a9b8c;
  transition: width 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
.hv-bars__hint {
  margin-top: 6px;
  font-size: 11px;
  color: #64748b;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.hv-bars--red .hv-bars__fill {
  background: #dc2626;
}
.hv-bars--amber .hv-bars__fill {
  background: #d97706;
}
.hv-bars--teal .hv-bars__fill {
  background: #1a9b8c;
}
.hv-bars--green .hv-bars__fill {
  background: #15803d;
}
.hv-bars--navy .hv-bars__fill {
  background: #12568a;
}

.hv-spark {
  width: 100%;
  height: 34px;
}
.hv-spark svg {
  width: 100%;
  height: 100%;
}
.hv-spark path {
  fill: none;
  stroke: #1a9b8c;
  stroke-width: 1.6;
}

.hv-timeline {
  position: relative;
  padding-left: 22px;
}
.hv-timeline::before {
  content: "";
  position: absolute;
  left: 7px;
  top: 4px;
  bottom: 4px;
  width: 2px;
  background: #edf1f6;
}
.hv-timeline__item {
  position: relative;
  padding-bottom: 16px;
}
.hv-timeline__item:last-child {
  padding-bottom: 0;
}
.hv-timeline__dot {
  position: absolute;
  left: -22px;
  top: 3px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 3px solid #ffffff;
  background: #a8b3c2;
  box-shadow: 0 0 0 1px #d8dee8;
}
.hv-timeline__time {
  font-size: 11px;
  color: #64748b;
}
.hv-timeline__title {
  font: 700 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #0f172a;
  margin-top: 2px;
}
.hv-timeline__body {
  margin-top: 4px;
  font-size: 12px;
  color: #334155;
}
.hv-timeline__item--denied .hv-timeline__dot {
  background: #dc2626;
}
.hv-timeline__item--resub .hv-timeline__dot {
  background: #7c3aed;
}
.hv-timeline__item--paid .hv-timeline__dot {
  background: #15803d;
}
.hv-timeline__item--pending .hv-timeline__dot {
  background: #d97706;
}
.hv-timeline__item--info .hv-timeline__dot {
  background: #12568a;
}
.hv-timeline__item--neutral .hv-timeline__dot {
  background: #64748b;
}

.hv-matrix {
  display: grid;
  gap: 3px;
  min-width: 0;
}
.hv-matrix__cell {
  aspect-ratio: 1;
  border-radius: 4px;
  display: grid;
  place-items: center;
  font: 700 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  background: #edf1f6;
  color: #334155;
}

.hv-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #cfd8e3;
  background: #f8fafc;
  color: #64748b;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-pill__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  flex: 0 0 6px;
}
.hv-pill--denied {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #fecaca;
  background: #fee2e2;
  color: #dc2626;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-pill--resub {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #c4b5fd;
  background: #ede9fe;
  color: #7c3aed;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-pill--paid {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #86efac;
  background: #dcfce7;
  color: #15803d;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-pill--pending {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #fcd34d;
  background: #fef3c7;
  color: #d97706;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-pill--info {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #93c5fd;
  background: #dbeafe;
  color: #12568a;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-pill--neutral {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #cfd8e3;
  background: #f8fafc;
  color: #64748b;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-pill--lg {
  padding: 5px 12px;
  font-size: 12px;
}

.hv-age {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #cfd8e3;
  background: #f8fafc;
  color: #64748b;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.hv-age--1 {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #86efac;
  background: #dcfce7;
  color: #15803d;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-age--2 {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #fcd34d;
  background: #fef3c7;
  color: #d97706;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-age--3 {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #fdba74;
  background: #ffedd5;
  color: #ea580c;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-age--4 {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #fca5a5;
  background: #fee2e2;
  color: #dc2626;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-age--5 {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  border: 1px solid #f87171;
  background: #fecaca;
  color: #991b1b;
  font: 800 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
}
.hv-age--5 {
  animation: hv-pulse 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes hv-pulse {
  0%, 100% {
    box-shadow: 0 0 0 0 rgba(153, 27, 27, 0.35);
  }
  50% {
    box-shadow: 0 0 0 5px rgba(153, 27, 27, 0);
  }
}
.hv-alert {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #cfd8e3;
  border-left: 3px solid #64748b;
  border-radius: 10px;
  background: #f8fafc;
  margin-bottom: 14px;
}
.hv-alert__icon {
  flex: 0 0 18px;
  margin-top: 1px;
}
.hv-alert__body {
  min-width: 0;
  flex: 1 1 auto;
}
.hv-alert__title {
  font: 700 13px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #0f172a;
}
.hv-alert__text {
  margin-top: 4px;
  font-size: 12px;
  color: #334155;
  line-height: 1.5;
}
.hv-alert__actions {
  margin-left: auto;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.hv-alert__close {
  border: 0;
  background: none;
  cursor: pointer;
  color: #64748b;
  padding: 0;
  line-height: 1;
}
.hv-alert__close:hover {
  color: #0f172a;
}
.hv-alert--denied {
  border-color: #fecaca;
  border-left-color: #dc2626;
  background: #fee2e2;
}
.hv-alert--denied .hv-alert__icon {
  color: #dc2626;
}
.hv-alert--resub {
  border-color: #c4b5fd;
  border-left-color: #7c3aed;
  background: #ede9fe;
}
.hv-alert--resub .hv-alert__icon {
  color: #7c3aed;
}
.hv-alert--paid {
  border-color: #86efac;
  border-left-color: #15803d;
  background: #dcfce7;
}
.hv-alert--paid .hv-alert__icon {
  color: #15803d;
}
.hv-alert--pending {
  border-color: #fcd34d;
  border-left-color: #d97706;
  background: #fef3c7;
}
.hv-alert--pending .hv-alert__icon {
  color: #d97706;
}
.hv-alert--info {
  border-color: #93c5fd;
  border-left-color: #12568a;
  background: #dbeafe;
}
.hv-alert--info .hv-alert__icon {
  color: #12568a;
}
.hv-alert--neutral {
  border-color: #cfd8e3;
  border-left-color: #64748b;
  background: #f8fafc;
}
.hv-alert--neutral .hv-alert__icon {
  color: #64748b;
}
@media (max-width: 640px) {
  .hv-alert {
    flex-wrap: wrap;
  }
  .hv-alert__actions {
    margin-left: 0;
    width: 100%;
  }
}

.hv-empty {
  display: grid;
  place-items: center;
  text-align: center;
  padding: 48px 22px;
  color: #64748b;
}
.hv-empty__icon {
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 12px;
  background: #f8fafc;
  color: #a8b3c2;
  margin-bottom: 12px;
}
.hv-empty__title {
  font: 700 14px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #334155;
}
.hv-empty__hint {
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  max-width: 300px;
}
.hv-empty__action {
  margin-top: 14px;
}
.hv-empty--sm {
  padding: 22px;
}

.hv-skel {
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, #eef2f7 25%, #f8fafc 37%, #eef2f7 63%);
  background-size: 400% 100%;
  animation: hv-shimmer 1.3s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}
.hv-skel--text {
  height: 12px;
}
.hv-skel--title {
  height: 18px;
  width: 40%;
}
.hv-skel--line {
  height: 12px;
  width: 100%;
}
.hv-skel--pill {
  height: 20px;
  width: 70px;
  border-radius: 999px;
}
.hv-skel--card {
  height: 84px;
  border-radius: 16px;
}
.hv-skel--row {
  height: 40px;
  border-radius: 8px;
}

@keyframes hv-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
.hv-skel-stack {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.hv-toasts {
  position: fixed;
  right: 18px;
  bottom: 18px;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: min(360px, 100vw - 22px);
}
@media (max-width: 640px) {
  .hv-toasts {
    right: 12px;
    left: 12px;
    bottom: 12px;
    max-width: none;
  }
}

.hv-toast {
  background: #ffffff;
  border: 1px solid #d8dee8;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(15, 35, 58, 0.07), 0 4px 14px rgba(15, 35, 58, 0.05);
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  box-shadow: 0 8px 28px rgba(15, 35, 58, 0.14);
  animation: hv-toast-in 0.22s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.hv-toast__icon {
  flex: 0 0 16px;
  margin-top: 2px;
}
.hv-toast__body {
  min-width: 0;
  font-size: 13px;
  color: #0f172a;
}
.hv-toast__close {
  margin-left: auto;
  border: 0;
  background: none;
  cursor: pointer;
  color: #a8b3c2;
}
.hv-toast--denied {
  border-left: 3px solid #dc2626;
}
.hv-toast--denied .hv-toast__icon {
  color: #dc2626;
}
.hv-toast--resub {
  border-left: 3px solid #7c3aed;
}
.hv-toast--resub .hv-toast__icon {
  color: #7c3aed;
}
.hv-toast--paid {
  border-left: 3px solid #15803d;
}
.hv-toast--paid .hv-toast__icon {
  color: #15803d;
}
.hv-toast--pending {
  border-left: 3px solid #d97706;
}
.hv-toast--pending .hv-toast__icon {
  color: #d97706;
}
.hv-toast--info {
  border-left: 3px solid #12568a;
}
.hv-toast--info .hv-toast__icon {
  color: #12568a;
}
.hv-toast--neutral {
  border-left: 3px solid #64748b;
}
.hv-toast--neutral .hv-toast__icon {
  color: #64748b;
}

@keyframes hv-toast-in {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
.hv-tip {
  position: relative;
}
.hv-tip::after {
  content: attr(data-tip);
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%) translateY(3px);
  padding: 5px 10px;
  border-radius: 8px;
  background: #0f172a;
  color: #fff;
  font: 600 11px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.12s cubic-bezier(0.4, 0, 0.2, 1), transform 0.12s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: 900;
}
.hv-tip:hover::after, .hv-tip:focus-visible::after {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.hv-scrim {
  position: fixed;
  inset: 0;
  z-index: 900;
  background: rgba(15, 35, 58, 0.42);
  backdrop-filter: blur(2px);
  animation: hv-scrim-in 0.18s cubic-bezier(0.4, 0, 0.2, 1) both;
}

@keyframes hv-scrim-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
.hv-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  width: min(520px, 100vw);
  display: flex;
  flex-direction: column;
  background: #ffffff;
  box-shadow: 0 24px 60px rgba(15, 35, 58, 0.22);
  animation: hv-drawer-in 0.24s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.hv-drawer__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #edf1f6;
  flex: 0 0 auto;
}
.hv-drawer__title {
  margin: 0;
  font: 700 16px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.hv-drawer__sub {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}
.hv-drawer__close {
  margin-left: auto;
  border: 0;
  background: none;
  cursor: pointer;
  color: #64748b;
  font-size: 20px;
  line-height: 1;
  padding: 0 4px;
}
.hv-drawer__close:hover {
  color: #0f172a;
}
.hv-drawer__body {
  flex: 1 1 auto;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 16px;
}
.hv-drawer__foot {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 14px 16px;
  border-top: 1px solid #edf1f6;
  background: #f8fafc;
}
.hv-drawer__section + .hv-drawer__section {
  margin-top: 18px;
}
.hv-drawer__section-title {
  font: 600 12px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 10px;
}
.hv-drawer--left {
  right: auto;
  left: 0;
  animation-name: hv-drawer-in-left;
}
.hv-drawer--wide {
  width: min(720px, 100vw);
}
@media (max-width: 640px) {
  .hv-drawer {
    width: 100vw;
  }
  .hv-drawer__head, .hv-drawer__body {
    padding: 12px;
  }
}

@keyframes hv-drawer-in {
  from {
    transform: translateX(100%);
  }
  to {
    transform: none;
  }
}
@keyframes hv-drawer-in-left {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: none;
  }
}
.hv-modal {
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: grid;
  place-items: center;
  padding: 16px;
  pointer-events: none;
}
.hv-modal__box {
  background: #ffffff;
  border: 1px solid #d8dee8;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 35, 58, 0.07), 0 4px 14px rgba(15, 35, 58, 0.05);
  pointer-events: auto;
  width: min(520px, 100%);
  max-height: calc(100vh - 28px * 2);
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(15, 35, 58, 0.22);
  animation: hv-modal-in 0.2s cubic-bezier(0.4, 0, 0.2, 1) both;
}
.hv-modal__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #edf1f6;
}
.hv-modal__title {
  margin: 0;
  font: 700 16px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.hv-modal__close {
  margin-left: auto;
  border: 0;
  background: none;
  cursor: pointer;
  color: #64748b;
  font-size: 20px;
  line-height: 1;
}
.hv-modal__close:hover {
  color: #0f172a;
}
.hv-modal__body {
  padding: 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
}
.hv-modal__foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 14px 16px;
  border-top: 1px solid #edf1f6;
  background: #f8fafc;
}
.hv-modal--wide .hv-modal__box {
  width: min(820px, 100%);
}

@keyframes hv-modal-in {
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.985);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
.hv-no-scroll {
  overflow: hidden;
}

.hv-t-muted {
  color: #64748b;
}

.hv-t-faint {
  color: #a8b3c2;
}

.hv-t-ink {
  color: #0f172a;
}

.hv-t-teal {
  color: #1a9b8c;
}

.hv-t-green {
  color: #15803d;
}

.hv-t-amber {
  color: #d97706;
}

.hv-t-red {
  color: #dc2626;
}

.hv-t-xs {
  font-size: 11px;
}

.hv-t-sm {
  font-size: 12px;
}

.hv-t-lg {
  font-size: 16px;
}

.hv-t-semi {
  font-weight: 600;
}

.hv-t-bold {
  font-weight: 700;
}

.hv-t-black {
  font-weight: 800;
}

.hv-t-upper {
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.hv-t-right {
  text-align: right;
}

.hv-t-center {
  text-align: center;
}

.hv-t-mono {
  font-family: "SFMono-Regular", "Roboto Mono", ui-monospace, monospace;
}

.hv-num {
  font-variant-numeric: tabular-nums;
}

.hv-truncate {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hv-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hv-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hv-mt-0 {
  margin-top: 0;
}

.hv-mb-0 {
  margin-bottom: 0;
}

.hv-mt-1 {
  margin-top: 4px;
}

.hv-mb-1 {
  margin-bottom: 4px;
}

.hv-mt-2 {
  margin-top: 6px;
}

.hv-mb-2 {
  margin-bottom: 6px;
}

.hv-mt-3 {
  margin-top: 8px;
}

.hv-mb-3 {
  margin-bottom: 8px;
}

.hv-mt-4 {
  margin-top: 10px;
}

.hv-mb-4 {
  margin-bottom: 10px;
}

.hv-mt-5 {
  margin-top: 12px;
}

.hv-mb-5 {
  margin-bottom: 12px;
}

.hv-mt-6 {
  margin-top: 14px;
}

.hv-mb-6 {
  margin-bottom: 14px;
}

.hv-mt-7 {
  margin-top: 16px;
}

.hv-mb-7 {
  margin-bottom: 16px;
}

.hv-mt-8 {
  margin-top: 18px;
}

.hv-mb-8 {
  margin-bottom: 18px;
}

.hv-mt-9 {
  margin-top: 22px;
}

.hv-mb-9 {
  margin-bottom: 22px;
}

.hv-mt-10 {
  margin-top: 28px;
}

.hv-mb-10 {
  margin-bottom: 28px;
}

.hv-m-0 {
  margin: 0;
}

.hv-flex {
  display: flex;
  min-width: 0;
}

.hv-inline {
  display: inline-flex;
  align-items: center;
}

.hv-w-full {
  width: 100%;
}

.hv-min-0 {
  min-width: 0;
}

.hv-nowrap {
  white-space: nowrap;
}

.hv-wrap {
  flex-wrap: wrap;
}

.hv-grow {
  flex: 1 1 auto;
}

.hv-hide {
  display: none !important;
}

.hv-card {
  background: #ffffff;
  border: 1px solid #d8dee8;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 35, 58, 0.07), 0 4px 14px rgba(15, 35, 58, 0.05);
}

.hv-soft {
  background: #f8fafc;
}

.hv-mintbg {
  background: #f0faf8;
}

.hv-rounded {
  border-radius: 16px;
}

.hv-shadow {
  box-shadow: 0 1px 3px rgba(15, 35, 58, 0.07), 0 4px 14px rgba(15, 35, 58, 0.05);
}

@media (max-width: 768px) {
  .hv-hide-mobile {
    display: none !important;
  }
}
@media (min-width: 769px) {
  .hv-only-mobile {
    display: none !important;
  }
}
@media (max-width: 1024px) {
  .hv-hide-tablet {
    display: none !important;
  }
}
.hv-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.hv-skip-link {
  position: absolute;
  left: -9999px;
}
.hv-skip-link:focus {
  left: 12px;
  top: 12px;
  z-index: 1200;
  padding: 10px 14px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 8px 28px rgba(15, 35, 58, 0.14);
}

.ib2-app {
  font-family: "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
}

.hide-on-print {
  display: inline-flex;
}
@media print {
  .hide-on-print,
  .overlay,
  .alert-modal,
  .alertmsg-modal,
  .aleart-modal {
    display: none !important;
  }
}

.overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1300;
}
.ib2-loader {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 48px rgba(15, 35, 58, 0.28);
  padding: 28px 36px;
  min-width: 300px;
  text-align: center;
}
.ib2-loader-coins {
  display: flex;
  justify-content: center;
  gap: 14px;
  font-size: 26px;
  margin-bottom: 12px;
  animation: ib2-bounce 1.2s ease-in-out infinite;
}
.ib2-loader-coins span:nth-child(2) { animation-delay: 0.15s; }
.ib2-loader-coins span:nth-child(3) { animation-delay: 0.3s; }
@keyframes ib2-bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
.ib2-loader-bar {
  height: 6px;
  border-radius: 999px;
  background: #e2e8f0;
  overflow: hidden;
  margin-bottom: 14px;
}
.ib2-loader-bar-fill {
  height: 100%;
  width: 40%;
  border-radius: 999px;
  background: linear-gradient(90deg, #1a9b8c, #34d3a4);
  animation: ib2-slide 1.4s ease-in-out infinite;
}
@keyframes ib2-slide {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(250%); }
}
.ib2-loader-title {
  font-weight: 800;
  font-size: 15px;
  color: #0f172a;
  margin-bottom: 4px;
}
.ib2-loader-quip {
  font-size: 13px;
  color: #64748b;
}

.ib2-col-tab-icon {
  font-size: 13px;
}
.ib2-col-tab-label {
  white-space: nowrap;
}

.ib2-th-title {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.ib2-th-extra {
  font-weight: 600;
  color: #94a3b8;
}
.ib2-th-search {
  width: 100%;
  margin-top: 4px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid #d7dee7;
  border-radius: 6px;
  background: #ffffff;
  color: #0f172a;
}
.ib2-th-search:focus-visible {
  outline: none;
  border-color: #1a9b8c;
  box-shadow: 0 0 0 3px rgba(26, 155, 140, 0.18);
}
.ib2-th-select {
  width: 100%;
  margin-top: 4px;
  padding: 4px 6px;
  font-size: 12px;
  border: 1px solid #d7dee7;
  border-radius: 6px;
  background: #ffffff;
  color: #0f172a;
}
.ib2-th-control {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
}
.ib2-th-control-text {
  white-space: nowrap;
}
.ib2-th-buttons {
  display: inline-flex;
  gap: 6px;
  margin-top: 4px;
}

.ib2-group-start {
  border-left: 1px solid #e2e8f0;
}

.EligibilityChecked {
  color: #15803d;
  cursor: pointer;
}
.EligibilityNotChecked {
  color: #0f172a;
  cursor: pointer;
}
.linkspan {
  cursor: pointer;
  color: #1a9b8c;
  font-weight: 600;
}
.linkspan:hover {
  text-decoration: underline;
}
.show-icon {
  color: #1a9b8c;
}
.success-icon {
  color: #15803d;
  margin-right: 4px;
}

.not-submit-style,
.denied-style,
.paid-style,
.forwarded-style,
.rejected-style,
.sent-style {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 999px;
  font: 800 10px "Inter", system-ui, -apple-system, "Segoe UI", sans-serif;
  white-space: nowrap;
  cursor: default;
}
.not-submit-style {
  border: 1px solid #cfd8e3;
  background: #f8fafc;
  color: #64748b;
}
.denied-style {
  border: 1px solid #fecaca;
  background: #fee2e2;
  color: #dc2626;
}
.paid-style {
  border: 1px solid #86efac;
  background: #dcfce7;
  color: #15803d;
}
.forwarded-style {
  border: 1px solid #fcd34d;
  background: #fef3c7;
  color: #d97706;
}
.rejected-style {
  border: 1px solid #fca5a5;
  background: #fef2f2;
  color: #b91c1c;
}
.sent-style {
  border: 1px solid #93c5fd;
  background: #dbeafe;
  color: #12568a;
}

.hv-field--search {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
}
.hv-field--search .hv-field__label {
  white-space: nowrap;
}
.autocomplete {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 40;
  min-width: 220px;
}
.autocomplete .list-style {
  list-style: none;
  margin: 4px 0 0;
  padding: 4px;
  background: #ffffff;
  border: 1px solid #d7dee7;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 35, 58, 0.16);
  max-height: 220px;
  overflow-y: auto;
}
.autocomplete .list-style li {
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
}
.autocomplete .list-style li:hover {
  background: #f0faf8;
  color: #0e6f5c;
}

.but {
  border: 1px solid #cfd8e3;
  background: #f8fafc;
  color: #0f172a;
  border-radius: 999px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.but:hover {
  border-color: #1a9b8c;
  color: #0e6f5c;
  background: #f0faf8;
}

.ib2-dp .react-datepicker__header {
  background: #f0faf8;
  border-bottom: 1px solid #b9e2dc;
}
.ib2-dp .react-datepicker__day--selected,
.ib2-dp .react-datepicker__day--keyboard-selected {
  background: #1a9b8c;
  color: #ffffff;
}
.ib2-dp .react-datepicker__day:hover {
  background: #dcf5f1;
}
.today-btn {
  border: 1px solid #1a9b8c;
  background: #ffffff;
  color: #0e6f5c;
  border-radius: 999px;
  padding: 4px 14px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}
.today-btn:hover {
  background: #1a9b8c;
  color: #ffffff;
}
.hv-date.default-selected {
  border-color: #1a9b8c;
  color: #0f172a;
}

.alert-modal,
.alertmsg-modal,
.aleart-modal {
  position: fixed;
  top: 18%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1400;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 48px rgba(15, 35, 58, 0.28);
  padding: 18px 22px;
  min-width: 300px;
  max-width: 480px;
  text-align: center;
}
.alert-modal p,
.alertmsg-modal p {
  margin: 0 0 12px;
  color: #0f172a;
  font-weight: 600;
}

.ml-5 { margin-left: 5px; }
.margin-left-auto { margin-left: auto; }
.margin-right-10px { margin-right: 10px; }

.hv-page--fill {
  min-height: 0;
}

.ib2-th-Provider,
.ib2-th-PolicyNumber,
.ib2-th-Order,
.ib2-th-ICD,
.ib2-th-CaseID {
  text-align: left;
  vertical-align: middle;
  white-space: nowrap;
}
.ib2-th-Provider {
  width: 170px;
  min-width: 170px;
}
.ib2-th-PolicyNumber {
  width: 130px;
  min-width: 130px;
}
.ib2-th-Order {
  width: 130px;
  min-width: 130px;
}
.ib2-th-ICD {
  width: 130px;
  min-width: 130px;
}
.ib2-th-CaseID {
  width: 130px;
  min-width: 130px;
}

.hv-tabbar {
  display: flex;
  flex-wrap: wrap;
  overflow-x: visible;
  row-gap: 6px;
}
.hv-tablebar {
  display: flex;
  flex: 1 1 auto;
  min-width: 260px;
}

.hv-worksheet tbody tr.table-warning td,
.hv-worksheet--pinned tbody tr.table-warning .hv-pin {
  background-color: #fff3cd !important;
}
.hv-worksheet tbody tr.table-warning:hover td,
.hv-worksheet--pinned tbody tr.table-warning:hover .hv-pin {
  background-color: #ffe69c !important;
}

`;

const LabBilling = () => {
  const today = new Date();

  const formatCurDate = format(today, "MM/dd/yyyy");

  // The page opens on the last week - a week back through today - so the grid
  // arrives with the range a biller actually works in already applied, instead
  // of a single day's worth or nothing at all.
  const defaultFromDate = format(subDays(today, 7), "MM/dd/yyyy");

  const [isHideChecked, setIsHideChecked] = useState(false);
  const [isClosedClaim, setIsClosedClaim] = useState(false);
  const [updatedTabledata, setUpdatedTableData] = useState([]);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState(defaultFromDate);
  const [endDate, setEndDate] = useState(formatCurDate);
  const [loading, setLoading] = useState(true);
  const [quipIndex, setQuipIndex] = useState(0);
  const [fromDate, setFromDate] = useState(defaultFromDate);

  const [ServiceLocations, setServiceLocations] = useState([]);
  const [selectedCMS33a, setSelectedCMS33a] = useState("0");
  const [selectedCMS33b, setSelectedCMS33b] = useState("0");
  const [selectedBillingNPI, setSelectedBillingNPI] = useState("0");
  const [selectedPracticeProvider, setSelectedPracticeProvider] = useState("0");
  const [selectedProvidersCategory, setSelectedProvidersCategory] =
    useState("0");
  const [selectedSearchFilter, setSelectedSearchFilter] = useState("");
  const [sFilterType, setsFilterType] = useState("");
  const [selectedLocation, setSelectedLocation] = useState("-1");

  const [selectedStatusRadioButton, setSelectedStatusRadioButton] =
    useState("0");
  const [selectedCheckedRadioButton, setSelectedCheckedRadioButton] =
    useState("0");
  const [selectedServiceLocation, setSelectedServicelocation] = useState("0");
  const [selectedFilterProvider, setSelectedFilterProvider] = useState("0");
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [selectedData, setSelectedData] = useState("");
  const [selectedProviderSuggestion, setSelectedProviderSuggestion] = useState(
    [],
  );
  const [locations, setLocations] = useState([]);
  const [cms33aData, setCMS33aData] = useState([]);
  const [cms33bData, setCMS33bData] = useState([]);
  const [providerCategory, setProviderCategory] = useState([]);
  const [searchFilterData, setSearchFilterData] = useState([]);
  const [practiceProvider, setPracticeProvider] = useState([]);
  const [statusRadioButton, setStatusRadioButton] = useState([]);
  const [defaultsOpen, setDefaultsOpen] = useState(true);
  const gridScrollRef = useRef(null);

  const [expandedIssueRows, setExpandedIssueRows] = useState([]);
  const toggleIssueRow = (rowId) =>
    setExpandedIssueRows((open) =>
      open.includes(rowId)
        ? open.filter((id) => id !== rowId)
        : [...open, rowId],
    );
  const [checkedRadioButton, setCheckedRadioButton] = useState([]);
  const [selectClearinghouse, setSelectClearingHouse] = useState([]);
  const [ClearingHouse, setClearingHouse] = useState("1");
  const [selectClearingHouseVal, setselectClearingHouseVal] = useState("");
  const [BillingNPIs, setBillingNPIs] = useState([]);
  const [rows, setRows] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [searchPatientText, setSearchPatientText] = useState("");
  const [searchProvidersText, setSearchProvidersText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [isTextBoxDisabled, setIsTextBoxDisabled] = useState(false);
  const [issuggestionBoxDisabled, setIsSuggestionBoxDisabled] = useState(true);
  const [sPatID, setSPatID] = useState("0");
  const [sClaimStatus, setsClaimStatus] = useState("0");
  const [sCheckedInStatus, setsCheckedInStatus] = useState(0);
  const [rowsValidation, setRowsValidation] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [tableDetails, setTableDetails] = useState([]);
  const [tableInsDetails, setTableInsDetails] = useState([]);
  const [checkEligibility, setCheckEligibility] = useState([]);
  const [selectAllDropDown, setSelectAllDropDown] = useState([]);
  const [searchPatientData, setSearchPatientData] = useState([]);
  const [suggestionProviders, setSuggestionProviders] = useState("");
  const [activeRow, setActiveRow] = useState(null);

  const [selectedRows, setSelectedRows] = useState([]);
  const [selectClearingHouseRow, setSelectClearingHouseRow] = useState([]);
  const [selectedLocationRows, setSelectedLocationRows] = useState([]);
  const [UnitChargeRows, setUnitChargeRows] = useState([]);
  const [unitCheckbox, setUnitCheckbox] = useState([]);
  const [diag4AllCheckbox, setDiag4AllCheckbox] = useState([]);
  const [diag3AllCheckbox, setDiag3AllCheckbox] = useState([]);
  const [diag2AllCheckbox, setDiag2AllCheckbox] = useState([]);
  const [diag1AllCheckbox, setDiag1AllCheckbox] = useState([]);
  const [mod3AllCheckbox, setMod3AllCheckbox] = useState([]);
  const [mod2AllCheckbox, setMod2AllCheckbox] = useState([]);
  const [mod1AllCheckbox, setMod1AllCheckbox] = useState([]);
  const [cptAllCheckbox, setCptAllCheckbox] = useState([]);
  const [providerAllCheckbox, setProviderAllCheckbox] = useState([]);
  const [insuranceAllCheckbox, setInsuranceAllCheckbox] = useState([]);
  const [closeClaimChecbox, setcloseClaimChecbox] = useState([]);
  const [closeAllClaimCheckbox, setcloseAllClaimCheckbox] = useState(false);
  const [filteredTableDetails, setFilteredTableDetails] =
    useState(tableDetails);
  const [providerDataList, setProviderDataList] = useState([]);
  const [cptDataList, setCptDataList] = useState([]);
  const [modDataList, setModDataList] = useState([]);
  const [mod2DataList, setMod2DataList] = useState([]);
  const [mod3DataList, setMod3DataList] = useState([]);
  const [diag1DataList, setDiag1DataList] = useState([]);
  const [diag2DataList, setDiag2DataList] = useState([]);
  const [diag3DataList, setDiag3DataList] = useState([]);
  const [diag4DataList, setDiag4DataList] = useState([]);
  const [selectedInsRows, setSelectedInsRows] = useState([]);
  const [selectedCPTCode, setSelectedCPTCode] = useState([]);
  const [selectedCPTPatientID, setSelectedCPTPatientID] = useState([]);
  const [selectedPatientID, setSelectedPatientID] = useState([]);
  const [selectedDiag1ID, setSelectedDiag1ID] = useState("");
  const [selectedDiag2ID, setSelectedDiag2ID] = useState("");
  const [selectedDiag3ID, setSelectedDiag3ID] = useState("");
  const [selectedDiag4ID, setSelectedDiag4ID] = useState("");
  const [searchByDOS, setSearchByDOS] = useState("");
  const [searchByPatient, setSearchByPatient] = useState("");
  const [searchByDOB, setSearchByDOB] = useState("");
  const [searchByInsuranceID, setSearchByInsuranceID] = useState("");
  const [searchByProvider, setSearchByProvider] = useState("");
  const [searchByCPT, setSearchByCPT] = useState("");
  const [searchByMod1, setSearchByMod1] = useState("");
  const [searchByMod2, setSearchByMod2] = useState("");
  const [searchByMod3, setSearchByMod3] = useState("");
  const [searchByICD1, setSearchByICD1] = useState("");
  const [searchByICD2, setSearchByICD2] = useState("");
  const [searchByICD3, setSearchByICD3] = useState("");
  const [searchByICD4, setSearchByICD4] = useState("");
  const [searchBySUD, setSearchBySUD] = useState("");

  const [selectedCptValue, setSelectedCptValue] = useState("");
  const [selectedMod1Value, setSelectedMod1Value] = useState("");
  const [selectedMod2ListVal, setSelectedMod2ListVal] = useState("");
  const [selectedMod3ListVal, setSelectedMod3ListVal] = useState("");
  const [selectedDiag1List, setSelectedDiag1List] = useState("");
  const [selectedDiag2List, setSelectedDiag2List] = useState("");
  const [selectedDiag3List, setSelectedDiag3List] = useState("");
  const [selectedDiag4List, setSelectedDiag4List] = useState("");

  const [cptValueList, setCptValueList] = useState([]);
  const [mod1List, setMod1List] = useState([]);
  const [mod2List, setMod2List] = useState([]);
  const [mod3List, setMod3List] = useState([]);
  const [diag1List, setDiag1List] = useState([]);
  const [diag2List, setDiag2List] = useState([]);
  const [diag3List, setDiag3List] = useState([]);
  const [diag4List, setDiag4List] = useState([]);

  const [syncedValue, setSyncedValue] = useState("");
  const [syncedUnitchargeVal, setSyncedUnitchargeVal] = useState("");

  const [selectedProviderText, setSelectedProviderText] = useState("");
  const [selectedCPTList, setSelectedCPTList] = useState("");
  const [selectedModList, setSelectedModList] = useState("");
  const [selectedMod2Value, setSelectedMod2Value] = useState("");
  const [selectedMod3Value, setSelectedMod3Value] = useState("");
  const [selectedDiag1Value, setSelectedDiag1Value] = useState("");
  const [selectedDiag2Value, setSelectedDiag2Value] = useState("");
  const [selectedDiag3Value, setSelectedDiag3Value] = useState("");
  const [selectedDiag4Value, setSelectedDiag4Value] = useState("");
  const [selectedUnitValue, setSelectedUnitValue] = useState("");
  const [selectedUnitChargeValue, setSelectedUnitChargeValue] = useState("");

  const [selectLocationID, setSelectLocationID] = useState("-1");

  const [showSelectErrorModal, setShowSelectErrorModal] = useState(false);
  const [showICDErrorModal, setShowICDErrorModal] = useState(false);
  const [ICDConfirmSaveModal, setICDConfirmSaveModal] = useState(false);

  const [showConfirmSaveModal, setShowConfirmSaveModal] = useState(false);
  const [showApprovalSaveModal, setShowApprovalSaveModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [msgSuccesmodal, setmsgSuccesmodal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteSuccessModal, setDeleteSuccessModal] = useState(false);
  const [closeClaimsModal, setCloseClaimsModal] = useState(false);
  const [showFaxModal, setShowFaxModal] = useState(false);
  const [savedRowCount, setSavedRowCount] = useState(0);
  const [deleteRowCount, setDeleteRowCount] = useState(0);
  const [successTimer, setSuccessTimer] = useState(10000);
  const [showFaxError, setShowFaxError] = useState(false);
  const [saveFaxError, setSaveFaxError] = useState(false);
  const [showFaxSaveMsg, setShowFaxSaveMsg] = useState(false);
  const [showFaxConfirmMsg, setShowFaxConfirmMsg] = useState(false);

  const [selectedProviderData, setselectedProviderData] = useState("");
  const [selectedMod1Data, setSelectedMod1Data] = useState("");
  const [selectedMod2Data, setSelectedMod2Data] = useState("");
  const [selectedMod3Data, setSelectedMod3Data] = useState("");

  const [selectedInsurances, setSelectedInsurances] = useState({});
  const [policyNumbers, setPolicyNumbers] = useState({});

  const [selectAllRowsChecked, setSelectAllRowsChecked] = useState(false);
  const [selectAllClearingHouseRow, setSelectAllClearingHouseRow] =
    useState(false);
  const [savedRows, setSavedRows] = useState([]);
  const [epin, setEpin] = useState("");
  const [verifiedEpin, setverifiedEpin] = useState("");
  const [isEPINVerifiedModal, setIsEPINVerifiedModal] = useState(false);
  const [isStartOpen, setIsStartOpen] = useState(false);
  const [isEndOpen, setIsEndOpen] = useState(false);
  const [showDownLoadErrorModal, setShowDownLoadErrorModal] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [isInStatus, setIsInStatus] = useState("");
  const [rowState, setRowState] = useState({});
  const [claimID, setClaimID] = useState("");
  const [practiceProvider1, setPracticeProvider1] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const rowsPerPage = 200;
  const [visibleRecords, setVisibleRecords] = useState(rowsPerPage);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [showCopaySuccessModal, setShowCopaySuccessModal] = useState(false);
  const [showPrintPopup, setShowPrintPopup] = useState(false);
  const [labDropDownData, setLabDropDownData] = useState([]);
  const [selectedInsValue, setselectedInsValue] = useState('');
  const [selectedModifier, setselectedModifier] = useState('SA');
  const [showDataModal, setShowDataModal] = useState(false);
  const [rowSelections, setRowSelections] = useState({});
  const [omitboxRows, setomitboxRows] = useState({});
  const [locationNPIError, setLocationNPIError] = useState('');
  const [providerNPIError, setProviderNPIError] = useState('');
  const [taxonomyCodeError, setTaxonomyCodeError] = useState('');
  const [locationError, setLocationError] = useState('');
  const [isLocChecked, setIsLocChecked] = useState(false);
  const [generateClaimClicked, setGenerateClaimClicked] = useState(false);
  const [selectedLocations2, setselectedLocations2] = useState('-1');
  const [practiceTaxonomycodes, setPracticeTaxonomycodes] = useState([]);
  const [searchByCodes, setSearchByCodes] = useState('');
  const [searchByCaseID, setSearchByCaseID] = useState('');
  const [searchByPolicy, setSearchByPolicy] = useState('');
  const [searchByCPT2, setSearchByCPT2] = useState('');
  const [searchByLocation, setSearchByLocation] = useState('');
 


  const handleCloseClaimModal = () => {
    setCloseClaimsModal(true);
  };
  const insuranceMap = useMemo(() => {
    const map = {};

    tableInsDetails.forEach((ins) => {
      if (!map[ins.PatientId]) {
        map[ins.PatientId] = [];
      }
      map[ins.PatientId].push(ins);
    });

    return map;
  }, [tableInsDetails]);

  const handleCheckeligibility = async (e, rowIndex) => {
    if (insuranceAllCheckbox.length === 0) {
      setShowSelectErrorModal(true);
      return;
    }

    const rowId = insuranceAllCheckbox[0];
    const item = filteredTableDetails.find((row) => row.CPTRowID === rowId);
    if (!item) return;
    const currentClaimId = item.ClaimId;
    setClaimID(currentClaimId);
    setShowSelectErrorModal(false);
    setIsLoading(true);

    const inputStruct = {
      PatientID: item.PatientId,
      PracticeID: userPracticeID,
      PolicyNumber: item.PolicyNumber + "",
      DOS: item.DOS,
      RowIndex: rowId,
    };
    const objPatientDetails = { objPatientDetails: inputStruct };
    const myInput = JSON.stringify(objPatientDetails);
    const url = `${APIConstants.PATIENT_PORTAL_URL}${CommonAPIConstants.GetPatientEligibility}?sInputJSON=${myInput}`;

    fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        if (response.status != 200) {
          console.log("response.status", response.status);
        } else return response.json();
      })

      .then((data) => {
        const responseData = data;
        const parsedData = JSON.parse(responseData);

        setRowState((prevState) => ({
          ...prevState,
          [currentClaimId]: {
            isLoading: false,
            isHidden: false,
            showIcon: true,
          },
        }));

        setCheckEligibility(parsedData);

        var RowIndex = parsedData.RowIndex;
        setIsInStatus(parsedData.InsStatus);
        const payerIDElement = document.querySelector(`.payer${RowIndex}`);

        const filteredClaims = filteredTableDetails.filter((item) => {
          if (item.ClaimId == claimID) {
            if (payerIDElement) {
              if (parsedData.InsStatus === "Active") {
                payerIDElement.style.color = "green";
              } else {
                payerIDElement.style.color = "red";
              }
            }
          }

          return true;
        });

        setFilteredTableDetails(filteredClaims);
      })
      .catch((error) => {
        console.error("An error occurred:", error);

        setRowState((prevState) => ({
          ...prevState,
          [claimID]: { isLoading: false, isHidden: false, showIcon: true },
        }));
      });
  };



  const handleOrderLinkClick = (item) => {
    setShowDataModal(true);
  };




  const GetPDFCMS = (item) => {

    const claimFilters = {

      PracticeID: userPracticeID,
      ClaimID: item.ClaimId,
      PatientID: item.PatientId,

    };


    const sInputJSON = JSON.stringify(claimFilters);



    const url = `${APIConstants.BASE_URL_BILLING}${CommonAPIConstants.GetPDFCMS1500}?sInputJSON=${sInputJSON}`;

    console.log("URL: ", url);

    fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    })

      .then(response => response.json())
      .then(data => {
        console.log('API response:' + CommonAPIConstants.GetPDFCMS1500, data);
        if (data && data) {

          const parsedData = JSON.parse(data);
          const url = parsedData.DocumentURL
          window.open(url, '_blank');


        }
      })
      .catch(error => {
        console.error(' PDF failed', error);
      });
  };


  const handleAllClearinghouseChange = (e) => {
    const selectedValue = e.target.value;
    setselectClearingHouseVal(selectedValue);

    for (const curIndex of selectClearingHouseRow) {
      const item = tableDetails[curIndex];
      item.ClearingHouse = selectedValue;
    }
  };
  const handleSelectChange = (e) => {
    const { value } = e.target;
    setSelectedServicelocation(value);
    for (const curIndex of selectedLocationRows) {
      const item = tableDetails[curIndex];
      item.ServiceLocationCode = value;
    }
  };
  const handleICDOpenModal = () => {
    if (
      diag1AllCheckbox.length === 0 &&
      diag2AllCheckbox.length === 0 &&
      diag3AllCheckbox.length === 0 &&
      diag4AllCheckbox.length === 0
    ) {
      setShowICDErrorModal(true);
    } else {
      setICDConfirmSaveModal(true);
    }
  };
  const openPrintPopup = () => {
    const initial = {};
    columns.forEach(col => {
      initial[col.key] = true;
    });

    setPrintColumnVisibility(initial);
    setShowPrintPopup(true);
  };
  const togglePrintColumn = (key) => {
    setPrintColumnVisibility(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const removeHtmlTags = (htmlString) => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlString;
    return tempElement.textContent || tempElement.innerText;
  };



  const getClassForClaimStatus = (claimStatusFlag) => {
    const cleanClaimStatusFlag = removeHtmlTags(claimStatusFlag);

    switch (cleanClaimStatusFlag) {
      case 'N':
        return 'not-submit-style';
      case 'D':
        return 'denied-style';
      case 'P':
        return 'paid-style';
      case 'F':
        return 'forwarded-style';
      case 'R':
        return 'rejected-style';
      case 'S':
        return 'sent-style';
      default:
        return '';
    }
  };



  const getTitleForClaimStatus = (claimStatusFlag) => {
    const cleanClaimStatusFlag = removeHtmlTags(claimStatusFlag);
    switch (cleanClaimStatusFlag) {
      case 'N':
        return 'Not-Billed';
      case 'D':
        return 'Denied';
      case 'P':
        return 'Paid';
      case 'F':
        return 'Forwarded';
      case 'R':
        return 'Rejected';
      case 'S':
        return 'Sent';
      default:
        return '';
    }
  };


  const toggleRow = (id) => {
    setRowSelections((prev) => {
      const row = prev[id] || {};
      return {
        ...prev,
        [id]: {
          selected: !row.selected,
          cpt1: row.cpt1 || false,
          cpt2: row.cpt2 || false,
        },
      };
    });
  };


  const toggleCPT1 = (id) => {
    setRowSelections((prev) => {
      const prevRow = prev[id] || {};
      const newCPT1 = !prevRow.cpt1;
      const CPT2 = prevRow.cpt2 || false;
      const shouldSelect = newCPT1 || CPT2;
      return {
        ...prev,
        [id]: {
          ...prevRow,
          selected: shouldSelect,
          cpt1: newCPT1,
        },
      };
    });
  };

  const toggleCPT2 = (id) => {
    setRowSelections((prev) => {
      const prevRow = prev[id] || {};
      const newCPT2 = !prevRow.cpt2;
      const CPT1 = prevRow.cpt1 || false;
      const shouldSelect = newCPT2 || CPT1;
      return {
        ...prev,
        [id]: {
          ...prevRow,
          selected: shouldSelect,
          cpt2: newCPT2,
        },
      };
    });
  };


  const handleSelectAllCPT1 = () => {
    const allSelected = filteredTableDetails.every(
      row => rowSelections[row.IntelliLabDetailsID]?.cpt1
    );

    const newState = {};

    filteredTableDetails.forEach(row => {
      const id = row.IntelliLabDetailsID;
      const prev = rowSelections[id] || {};
      const newCPT1 = !allSelected;
      const CPT2 = prev.cpt2 || false;
      newState[id] = {
        ...prev,
        cpt1: newCPT1,
        selected: newCPT1 || CPT2,
      };
    });

    setRowSelections(newState);
  };

  const handleSelectAllCPT2 = () => {
    const allSelected = filteredTableDetails.every(
      row => rowSelections[row.IntelliLabDetailsID]?.cpt2
    );

    const newState = {};

    filteredTableDetails.forEach(row => {
      const id = row.IntelliLabDetailsID;
      const prev = rowSelections[id] || {};
      const newCPT2 = !allSelected;
      const CPT1 = prev.cpt1 || false;
      newState[id] = {
        ...prev,
        cpt2: newCPT2,
        selected: newCPT2 || CPT1,
      };
    });

    setRowSelections(newState);
  };



  const handleSelectOmitBOXAllChange = () => {
    const allSelected = filteredTableDetails.every(
      (item) => omitboxRows[item.IntelliLabDetailsID] === 1
    );
    let updated = { ...omitboxRows };
    filteredTableDetails.forEach((item) => {
      updated[item.IntelliLabDetailsID] = allSelected ? 0 : 1;
    });
    setomitboxRows(updated);
  };
  const handleOmitBoxRowCheckboxChange = (id) => {
    setomitboxRows((prev) => ({
      ...prev,
      [id]: prev[id] === 1 ? 0 : 1,
    }));
  };


    const handleSelectAllRows = () => {
        const allSelected = filteredTableDetails.every(
            row => rowSelections[row.IntelliLabDetailsID]?.selected
        );

        let updated = {};
        filteredTableDetails.forEach(row => {
            const id = row.IntelliLabDetailsID;
            updated[id] = {
                ...rowSelections[id],
                selected: !allSelected,
                cpt1: rowSelections[id]?.cpt1 || false,
                cpt2: rowSelections[id]?.cpt2 || false,
            };
        });
        setRowSelections(updated);
    };



  const handlePDFClick = (e, pdfName) => {
   
    const url = pdfName;

    window.open(url, '_blank');
  };

  const columns = [
    {
      key: "#",
      label: "#",
      render: (item, idx) => {
        const errors = (item.DataValidationMessage || "")
          .split("\n")
          .map((msg) => msg.trim())
          .filter(Boolean);
        const open = expandedIssueRows.includes(item.CPTRowID);
        return (
          <>
            <span className="hv-td-muted">{idx + 1}</span>
            {errors.length > 0 && item.iClosed != 1 && (
              <button
                type="button"
                className="hv-errcount hide-on-print"
                aria-expanded={open}
                title={
                  open
                    ? "Hide validation messages"
                    : `Show ${errors.length} validation message${errors.length > 1 ? "s" : ""}`
                }
                onClick={(e) => {
                  e.stopPropagation();
                  toggleIssueRow(item.CPTRowID);
                }}
              >
                {errors.length}
              </button>
            )}
          </>
        );
      },
    },

    {
      key: "AppLocationName",
      label: "Location",
      isLink: true,
      render: (item) => <label>{item.LocationNickname}</label>,
    },

    {
      key: "DOS",
      label: "DOS",
      isLink: true,
      hasSearch: true,
      searchValue: searchByDOS,
      setSearchValue: setSearchByDOS,
      render: (item) => (
        <label className="linkspan" onClick={() => handleDosLinkClick(item)}>
          {getDosText(item)}
        </label>
      ),
    },

    {
      key: "PatientName",
      label: "Patient Name(2)",
      isLink: true,
      hasSearch: true,
      searchValue: searchByPatient,
      setSearchValue: setSearchByPatient,
      render: (item) => (
        <>
          <label
            className={`linkspan ${item.EligibilityCheckCount > 0
              ? "EligibilityChecked"
              : "EligibilityNotChecked"
              }`}
            onClick={() => handlePatLinkClick(item)}
            title={item.LastName}
          >
            {item.LastName}, {item.FirstName}
          </label>
          {item.PatSecInscount > 0 && (
            <span>
              <FontAwesomeIcon
                className="show-icon"
                style={{ marginLeft: "10px" }}
                icon={faShieldHalved}
              />
            </span>
          )}
        </>
      ),
    },


    {
      key: "DOB",
      label: "DOB(3)",
      hasSearch: true,
      searchValue: searchByDOB,
      setSearchValue: setSearchByDOB,
      render: (item) => <label>{item.DOB}</label>,
    },

    {
      key: "SelectedInsuranceName",
      label: "Insurance",
      isLink: true,
      hasSearch: true,
      searchValue: searchByInsuranceID,
      setSearchValue: setSearchByInsuranceID,

      render: (item, index) => {
        const rowId = item.CPTRowID;
        const selectedInsId = selectedInsurances[rowId];
        const selectedInsurance = tableInsDetails.find(
          (ins) => String(ins.InsId) === String(selectedInsId),
        );
        return (
          <div className="hv-cellpair">
            {(() => {
              const options = getFilteredInsDetailsForItem(item);
              const id = item.IntelliLabDetailsID;
              return options.length > 0 ? (
                <select
                  name="select"
                  style={{ width: '130px' }}
                  value={selectedInsurances[id] || ''}
                  onChange={(e) => handleInsuranceChange(id, e.target.value)}
                >

                  {options.map((ins, i) => (
                    <option key={i} value={ins.PrimaryInsID || ''}>
                      {ins.InsCompNickName || ''}
                    </option>
                  ))}
                </select>
              ) : (
                <select name="select" style={{ width: '130px' }} value=''>
                  <option value=''>No Insurance</option>
                </select>
              );
            })()}

          </div>
        );
      },
    },

    {
      key: "PolicyNumber",
      label: "Policy Number",
      hasSearch: true,
      searchValue: searchByPolicy,
      setSearchValue: setSearchByPolicy,

      render: (item, idx) => (
        <>
          <label>{policyNumbers[item.IntelliLabDetailsID] || ''}</label>
        </>
      ),
    },

    {
      key: "Order Code",
      label: "Order Code",
      hasSearch: true,
      searchValue: searchByCodes,
      setSearchValue: setSearchByCodes,

      render: (item, idx) => (
        <>
          <label onClick={handleOrderLinkClick}><Link>{item.OrderCodes}</Link></label>
        </>
      ),
    },



    {
      key: "ICD",
      label: "ICD",
      hasSearch: true,
      searchValue: searchByICD1,
      setSearchValue: setSearchByICD1,
      render: (item, idx) => (
        <>

          <label>{item.ICDCodes}</label>

        </>
      ),
    },


    {
      key: "CaseID",
      label: "Case ID",
      hasSearch: true,
      searchValue: searchByCaseID,
      setSearchValue: setSearchByCaseID,
      render: (item, idx) => (
        <>

          <label>{item.RequisitionNumber}</label>

        </>
      ),
    },


    {
      key: "Provider",
      label: "Rendering Provider",
      hasSearch: true,
      searchValue: searchByProvider,
      setSearchValue: setSearchByProvider,
      render: (item, idx) => (
        <>

          <label>{item.ProviderName}</label>

        </>
      ),
    },



    {
      key: "Cpt Code",
      label: "Cpt Code",
      hasSearch: true,
      searchValue: searchByCPT,
      setSearchValue: setSearchByCPT,
      headerCheckbox: {
        checked:
          filteredTableDetails.length > 0 &&
          filteredTableDetails.every(
            row => rowSelections[row.IntelliLabDetailsID]?.cpt1
          ),

        onChange: () => handleSelectAllCPT1('all'),
      },
      render: (item, idx) => {
        return (
          <>
            <label>{item.CPTCode1}</label>

            {item.CPTCode1 !== "" && item.CPTCode1 !== null && (item.ScrClaimStatus === null ? (

              <>

                <FontAwesomeIcon
                  title='Claim Not Present'
                  className="text-danger ml-5 hide-on-print"
                  icon={faTriangleExclamation}
                />

              </>

            ) : (
              <>

                {
                  !item.SCRClaimStatus ? (
                    <>

                    </>
                  ) : (
                    <>

                      <label
                        className={getClassForClaimStatus(item.SCRClaimStatus)}
                        title={getTitleForClaimStatus(item.SCRClaimStatus)}>{item.SCRClaimStatus}
                      </label>

                    </>
                  )
                }

              </>
            ))
            }


            {((item.SCRClaimID === null || item.SCRClaimID === "") && item.CPTCode1) && (
              <input
                type="checkbox"
                className="ml-5"
                checked={!!rowSelections[item.IntelliLabDetailsID]?.cpt1}
                onChange={() => toggleCPT1(item.IntelliLabDetailsID)}
              />
            )}


          </>
        );
      },
    },

    {
      key: "Cpt Code 2",
      label: "Cpt Code 2",
      hasSearch: true,
      searchValue: searchByCPT2,
      setSearchValue: setSearchByCPT2,
      headerCheckbox: {
        checked:
          filteredTableDetails.length > 0 &&
          filteredTableDetails.every(
            row => rowSelections[row.IntelliLabDetailsID]?.cpt2
          ),

        onChange: () => handleSelectAllCPT2('all'),
      },
      render: (item, idx) => {
        return (
          <>
            <label>{item.CPTCode2}</label>

            {item.CPTCode2 !== "" && item.CPTCode2 !== null && (item.CNFClaimStatus === null ? (

              <>

                <FontAwesomeIcon
                  title='Claim Not Present'
                  className="text-danger ml-5 hide-on-print"
                  icon={faTriangleExclamation}
                />

              </>

            ) : (
              <>


                {
                  !item.CNFClaimStatus ? (
                    <>

                    </>
                  ) : (
                    <>

                      <label
                        className={getClassForClaimStatus(item.CNFClaimStatus)}
                        title={getTitleForClaimStatus(item.CNFClaimStatus)}>{item.CNFClaimStatus}
                      </label>

                    </>
                  )
                }

              </>
            ))
            }


            {((
              item.CNFClaimID === null ||
              item.CNFClaimID === "") && item.CPTCode2) && (
                <input
                  type="checkbox"
                  className="ml-5"
                  checked={!!rowSelections[item.IntelliLabDetailsID]?.cpt2}
                  onChange={() => toggleCPT2(item.IntelliLabDetailsID)}
                />
              )}



          </>
        );
      },
    },


    {
      key: "Omit Box 32",
      label: "Omit Box 32",
      headerCheckbox: {
        onChange: () => handleSelectOmitBOXAllChange('all'),
      },
      render: (item, idx) => (
        <>
          {}
          <div className="hide-on-print">

            <input
              type="checkbox"
              checked={omitboxRows[item.IntelliLabDetailsID] == 1}
              onChange={() => handleOmitBoxRowCheckboxChange(item.IntelliLabDetailsID)}
            />

          </div>

        </>
      ),
    },


    {
      key: "Requisition",
      label: "Requisition",
     
      headerCheckbox: {
        checked:
          filteredTableDetails.length > 0 &&
          filteredTableDetails.every(
            row => rowSelections[row.IntelliLabDetailsID]?.selected
          ),
      
        onChange : ()=> handleSelectAllRows('all'),

    },
    render: (item, idx) => (
      <>
        <div style={{ display: 'flex' }}>
          {item.IsOrderSubmitted == 1 && (
            <a
              href="#"
              onClick={(e) => handlePDFClick(e, item.ReqPDFURL)}
              style={{ cursor: 'pointer', color: 'black' }}
            >
              <FontAwesomeIcon icon={faFileLines} />
            </a>
          )}

          <div style={{ justifyContent: 'right', marginLeft: '10px' }}>
            <input
              type="checkbox"
              checked={!!rowSelections[item.IntelliLabDetailsID]?.selected}
              onChange={() => toggleRow(item.IntelliLabDetailsID)}
            />
          </div>
        </div>

      </>
    ),
    },

  {
    key: "Result",
    label: "Result",

    render: (item, idx) => (
      <>
        {item.ResultPDFName && item.ResultPDFName !== '' && (

          <>
            <FontAwesomeIcon icon={faCircleCheck} className='success-icon ' />
            <a
              href="#"
              onClick={(e) => handlePDFClick(e, item.ResPDFURL)}
              style={{ cursor: 'pointer' }}
            >
              <FontAwesomeIcon className=''
                style={{ marginLeft: '8px', color: 'red' }}
                onClick={exportToPDF}
                icon={faFilePdf}
              />
            </a>
          </>
        )}


      </>
    ),
    },
  ];
const CONTEXT_KEYS = ["#", "AppLocationName", "DOS", "PatientName"];
const TRAILING_KEYS = ["Select"];

const DYNAMIC_VIEW_KEYS = [
  "ProviderDisplay",
  "DOB",
  "SelectedInsuranceName",
  "ProvderName",
  "CPT",
  "Mod1",
  "Mod2",
  "Mod3",
  "ICDCode",
  "ICDCode2",
  "ICDCode3",
  "ICDCode4",
  "Unit",
  "Unitcharge",
  "TotalCharge",
  "CopayAmount",
  "ClaimId",
  "ClearingHouse",
  "ServiceLocationCode",
];

const GROUP_START_KEYS = [
  "ProviderDisplay",
  "ProvderName",
  "CPT",
  "ICDCode",
  "Unit",
  "ClaimId",
];

const columnTabs = [
  {
    id: "claim-details",
    label: "Requisition details",
    icon: faIdCard,
    keys: [
      ...CONTEXT_KEYS,
      "ProviderDisplay",
      "DOB",
      "SelectedInsuranceName",
      "PolicyNumber",
      "Order Code",
      "ICD",
      "CaseID",
      "Provider",
      ...TRAILING_KEYS,
    ],
  },
  {
    id: "Generate Claim",
    label: "Generate Claims",
    icon: "✓",
    keys: [
      ...CONTEXT_KEYS,

      "Cpt Code",
      "Cpt Code 2",
      "Omit Box 32",
      "Requisition",
      "Result",

      ...TRAILING_KEYS,
    ],
  },
  {
    id: "all",
    label: "All",
    icon: faListCheck,
    keys: [
      ...CONTEXT_KEYS,
      "ProviderDisplay",
      "DOB",
      "SelectedInsuranceName",
      "PolicyNumber",
      "Order Code",
      "ICD",
      "CaseID",
      "Provider",
      "Cpt Code",
      "Cpt Code 2",
      "Omit Box 32",
      "Requisition",
      "Result",
      ...TRAILING_KEYS,
    ],
  },



];

const [activeColumnTab, setActiveColumnTab] = useState("claim-details");
// Derived, not state: storing this meant every tab click rendered twice — once for
// setActiveColumnTab and again for the setSelectedColumns that followed in an effect.
const selectedColumns =
  columnTabs.find((t) => t.id === activeColumnTab)?.keys ?? columnTabs[0].keys;
useEffect(() => {
  if (gridScrollRef.current)
    gridScrollRef.current.scrollLeft = 0;
}, [activeColumnTab]);
const [printColumnVisibility, setPrintColumnVisibility] = useState(() =>
  Object.fromEntries(columns.map(col => [col.key, true]))
);
const handleSelectAll = () => {
  const updated = {};
  columns.forEach(col => {
    updated[col.key] = true;
  });

  setPrintColumnVisibility(updated);
};
const handleDeselectAll = () => {
  const updated = {};
  columns.forEach(col => {
    updated[col.key] = false;
  });

  setPrintColumnVisibility(updated);
};
const allSelected = columns.every(col => printColumnVisibility[col.key]);
const noneSelected = columns.every(col => !printColumnVisibility[col.key]);
const loadMoreRecords = useCallback(() => {
  setVisibleRecords((prev) => {
    const next = Math.min(prev + rowsPerPage, filteredTableDetails.length);

    if (next > prev) {
      setPopupMessage(
        `${next} records out of ${filteredTableDetails.length} loaded`,
      );
      setShowPopup(true);

      setTimeout(() => {
        setShowPopup(false);
      }, 3000);
    }

    return next;
  });
}, [filteredTableDetails.length]);

const handleScroll = useCallback(() => {
  const isAtBottom =
    window.innerHeight + window.scrollY >=
    document.documentElement.scrollHeight;

  if (isAtBottom) {
    loadMoreRecords();
  }
}, [loadMoreRecords]);

useEffect(() => {
  setVisibleRecords(rowsPerPage);
}, [filteredTableDetails]);

useEffect(() => {
  if (!loading) return;
  const id = setInterval(() => {
    setQuipIndex((i) => (i + 1) % BILLING_QUIPS.length);
  }, 1600);
  return () => clearInterval(id);
}, [loading]);

const userData = useSelector((state) => state.user.user);

localStorage.setItem("userData", JSON.stringify(userData));

var userPracticeID = userData ? userData.practiceID : null;

var userId = userData ? userData.userID : null;

const sAuthToken = useSelector((state) => state.user.token);

useEffect(() => {
  document.title = "LabBilling";

  let timerId;

  if (
    showSuccessModal ||
    deleteSuccessModal ||
    msgSuccesmodal ||
    isEPINVerifiedModal ||
    showCopaySuccessModal
  ) {
    timerId = setTimeout(() => {
      setShowSuccessModal(false);
      setDeleteSuccessModal(false);
      setmsgSuccesmodal(false);
      setIsEPINVerifiedModal(false);
      setShowCopaySuccessModal(false);
    }, successTimer);
  }

  return () => {
    clearTimeout(timerId);
  };
}, [
  showSuccessModal,
  successTimer,
  deleteSuccessModal,
  msgSuccesmodal,
  isEPINVerifiedModal,
  showCopaySuccessModal,
]);

const handleFromDateChange = (date) => {
  if (date) {
    const formattedDate = format(date, "MM/dd/yyyy");
    setFromDate(formattedDate);
  } else {
    setFromDate(null);
  }
};

const handleStartDateChange = (date) => {
  if (date) {
    const formattedDate = format(date, "MM/dd/yyyy");
    setStartDate(formattedDate);
    setEndDate(formattedDate);
    setIsStartOpen(false);
  } else {
    setStartDate(null);
  }
};

const handleEndDateChange = (date) => {
  if (date) {
    const formattedDate = format(date, "MM/dd/yyyy");
    setEndDate(formattedDate);
    setIsEndOpen(false);
  } else {
    setEndDate(null);
  }
};

const getMonths = (selectedYear) => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  return Array.from({ length: 12 }, (v, k) => {
    if (selectedYear === currentYear && k > currentMonth) {
      return null;
    }
    return (
      <option key={k} value={k}>
        {format(new Date(selectedYear, k), "MMMM")}
      </option>
    );
  }).filter((option) => option !== null);
};

const handleStartDoneClick = () => {
  setIsStartOpen(false);
};

const handleTodayClick = (dateType) => {
  const today = new Date();

  if (dateType === "start" && startDate) {
    setStartDate(today);
    setEndDate(today);
    setIsStartOpen(false);
  }

  if (dateType === "end" && endDate) {
    setEndDate(today);
    setIsEndOpen(false);
  }
};

const CalendarContainer = ({ className, children }) => (
  <div
    className={`react-datepicker ib2-dp ${className || ""}`}
    style={{
      zIndex: "99px",
      backgroundColor: "white",
      border: "1px solid lightgray",
    }}
  >
    {children}
    <div
      style={{
        justifyContent: "center",
        padding: "10px",
        borderTop: "1px solid #ddd",
        marginLeft: "65px",
      }}
    >
      <button
        type="button"
        className="today-btn"
        onClick={() => handleTodayClick("end")}
      >
        Today
      </button>
    </div>
  </div>
);

const CalendarStartContainer = ({ className, children }) => (
  <div
    className={`react-datepicker ib2-dp ${className || ""}`}
    style={{
      zIndex: "99px",
      backgroundColor: "white",
      border: "1px solid lightgray",
    }}
  >
    {children}
    <div
      style={{
        justifyContent: "center",
        padding: "10px",
        borderTop: "1px solid #ddd",
        marginLeft: "65px",
      }}
    >
      <button
        type="button"
        className="today-btn"
        onClick={() => handleTodayClick("start")}
      >
        Today
      </button>
    </div>
  </div>
);

const convertDateFormat = (dateString) => {
  const [year, month, day] = dateString.split("-");
  return `${month}/${day}/${year}`;
};

const handleHideCheckboxChange = () => {
  setIsHideChecked(!isHideChecked);

  if (isHideChecked) {
    setStartDate(formatCurDate);
    setEndDate(formatCurDate);
  } else {
    setStartDate("01/1/2024");
    setEndDate(formatCurDate);
  }
};

const handleCloseClaimCheckbox = () => {
  setIsClosedClaim(!isClosedClaim);
};

useEffect(() => {
  getProvider();
}, [userData]);

const getProvider = () => {
  const url =
    APIConstants.COMMON_BASE_URL +
    `CommonData/BillingProviders?sInputJSON={"PracticeID":"${userPracticeID}"}`;
  const response = fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${sAuthToken}`,
    },
  })
    .then((response) => response.json())
    .then((data) => {
      setPracticeProvider(data);
    })

    .catch((error) => {
      console.error("Error fetching data: ", error);
    });
};

useEffect(() => {
  const url = `${APIConstants.COMMON_BASE_URL}${IntelliBillConstants.GetIntelliDropDowns}?sInputJSON={PracticeID:${userPracticeID},UserId:${userId} }`;

  fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  })
    .then((response) => {
      if (response.status != 200) {
        console.log("response.status", response.status);
      } else return response.json();
    })

    .then((data) => {
      const parsedData = JSON.parse(data);

      setSelectAllDropDown(parsedData);

      setLocations(JSON.parse(parsedData.LocationDetails));

      setCMS33aData(JSON.parse(parsedData.CMSbox33aData));
      setCMS33bData(JSON.parse(parsedData.CMSbox33bData));
      setBillingNPIs(JSON.parse(parsedData.PracticeBillingNPIs));
      setServiceLocations(JSON.parse(parsedData.ServiceLocation));
      setPracticeProvider1(JSON.parse(parsedData.PracticeProviders));
      setProviderCategory(JSON.parse(parsedData.ProvidersCategories));
      setSearchFilterData(JSON.parse(parsedData.SearchByFilters));
      setStatusRadioButton(JSON.parse(parsedData.StatusRadioButtons));
      setCheckedRadioButton(JSON.parse(parsedData.CheckedInRadioButtons));
      setLoading(false);
    })

    .catch((error) => {
      console.error("Error fetching data: ", error);
    });
}, [userData]);

const filteredOptions = ServiceLocations.filter(
  (option) => option.ID !== "0",
);



useEffect(() => {


  const response = fetch(APIConstants.BASE_URL + LabBillingConstants.GetLabDropDown, {
    method: 'POST',
    headers: {

      'Accept': 'application/json',
      'Content-Type': 'application/json'

    },


  })

    .then((response) => response.json())
    .then((data) => {
      const parsedData = JSON.parse(data.d);

      const LabData = parsedData.Data

      setLabDropDownData(LabData)



    })
    .catch((error) => {
      console.error('Error fetching patient names:', error);
    });

}, []);


useEffect(() => {
  if (showFaxModal) {
    setRows([]);
    resetDialogValues();
  }
}, [showFaxModal]);

const resetDialogValues = () => {
  setSelectedProviderText("");
  setSelectedCPTList("");
  setSelectedModList(null);
  setSelectedUnitValue("");
  setSelectedUnitChargeValue("");
  setSelectedDiag1Value("");
  setSelectedDiag2Value("");
  setSelectedDiag3Value("");
  setSelectedDiag4Value("");
  setSelectedMod2Value("");
  setSelectedMod3Value("");
  setSaveFaxError(false);
  setShowFaxError(false);
  setShowFaxSaveMsg(false);
};

const handleLocationChange = (e, item) => {
  const newSelectedLocation = e.target.value;
  setSearchByLocation(newSelectedLocation || item.LocationId);
  setSelectLocationID(newSelectedLocation);

  localStorage.setItem("selectedLocation", newSelectedLocation);
};

const handleSuggestionSelect = (item) => {
  setSearchText(item.label);

  setSelectedData(item.label);

  setSuggestions([]);

  setSPatID(item.val);
};

const handleInputKeyPress = (event) => {
  if (event.key === "Backspace") {
    if (searchText === "" && suggestions.length > 0) {
      setSuggestions([]);
      setSPatID("");
    } else {
      const updatedSearchText = searchText.slice(0, -1);
      setSearchText(updatedSearchText);

      fetchSuggestions(updatedSearchText);
    }
  } else if (event.key !== "Enter") {
    setSearchText(event.target.value);
    fetchSuggestions(event.target.value);
  }
};

const fetchSuggestions = (text) => {
  setSPatID("");

  if (selectedSearchFilter === "Pat") {
    fetchPatientNames(text);
  } else if (selectedSearchFilter === "Insurance") {
    fetchInsuranceCompanies(text);
  } else if (selectedSearchFilter === "Provider") {
    fetchProviders(text);
  } else if (selectedSearchFilter === "" && searchText === "") {
    setSPatID("");
    handleTableDataClick();
  }
};

useEffect(() => {
  setIsTextBoxDisabled(selectedSearchFilter === "");

  setSearchText("");
  setSPatID("");
}, [selectedSearchFilter]);

const fetchPatientNames = () => {
  var inputJSON = {
    PracticeID: userPracticeID,
    SearchKey: searchText,
    SearchBy: 0,
    isFromLogin: 0,
  };

  var sJSONInput = JSON.stringify(inputJSON);

  const url = `${APIConstants.BASE_URL_SEARCH}${CommonAPIConstants.GetAllPatientNames}?sJSONInput=${sJSONInput}`;

  fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  })
    .then((response) => {
      if (response.status != 200) {
        console.log("response.status", response.status);
      } else return response.json();
    })
    .then((data) => {
      const parsedData = JSON.parse(data);
      const patientData = parsedData.Data;

      if (Array.isArray(patientData)) {
        setSuggestions(
          patientData.map((item) => ({
            label: `${item.LastName}, ${item.FirstName}`,
            val: item.PatientID,
          })),
        );
      } else {
        setSuggestions([]);
        setIsTextBoxDisabled(false);
      }
    })
    .catch((error) => {
      console.error("Error fetching patient names:", error);
    });
};

const fetchSuggestionPatientNames = () => {
  if (!setSearchPatientText) {
    setSearchPatientData([]);
    return;
  }

  var inputJSON = {
    PracticeID: userPracticeID,
    SearchKey: searchPatientText,
    SearchBy: 0,
    isFromLogin: 0,
  };

  var sJSONInput = JSON.stringify(inputJSON);
  const url = `${APIConstants.BASE_URL_SEARCH}${CommonAPIConstants.GetAllPatientNames}?sJSONInput=${sJSONInput}`;
  fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  })
    .then((response) => {
      if (response.status != 200) {
        console.log("response.status", response.status);
      } else return response.json();
    })
    .then((data) => {
      const parsedData = JSON.parse(data);

      const patientData = parsedData.Data;

      if (Array.isArray(patientData)) {
        setSearchPatientData(
          patientData.map((item) => ({
            label: `${item.LastName}, ${item.FirstName}`,
            val: item.PatientID,
          })),
        );

        setSelectedCPTPatientID(patientData.val);
      } else {
        setSearchPatientData([]);
      }
    })
    .catch((error) => {
      console.error("Error fetching patient names:", error);
    });
};

const isMounted = useRef(true);

useEffect(() => {
  if (isMounted.current) {
    isMounted.current = false;
  } else {
    fetchSuggestionPatientNames(searchPatientText);
  }
}, [searchPatientText]);

const handlePatientSuggestion = (suggestion) => {
  setSearchPatientText(`${suggestion.label}`);
  setSelectedSuggestion(suggestion.label);
  setSelectedCPTPatientID(suggestion.val);
  setSearchPatientData([]);
  setShowFaxModal(true);
  setSelectedPatientID(suggestion.val);
};

const handleKeyDown = (e) => {
  if (e.key === "Backspace") {
    if (searchPatientText === "" && searchPatientData.length > 0) {
      setSearchPatientData([]);
    } else {
      fetchSuggestionPatientNames(searchPatientText.slice(0, -1));
    }
  }
};

const fetchProviders = () => {
  var inputStruct = {
    sProviderName: searchText,
    sPracticeID: userPracticeID,
    LoggedInUserDetails: userData,
  };

  var myInput = { sInputJSON: JSON.stringify(inputStruct) };

  var myInputData = JSON.stringify(myInput);

  const response = fetch(
    APIConstants.BASE_URL + IntelliBillConstants.GetIntelliProvidersNames,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },

      body: myInputData,
    },
  )
    .then((response) => response.json())
    .then((data) => {
      const parsedData = JSON.parse(data.d);

      const providersData = parsedData.Data;

      if (Array.isArray(providersData)) {
        setSuggestions(
          providersData.map((item) => ({
            label: item.split("$")[0].split("|")[1].trim(),
            val: item.split("$")[1].trim(),
          })),
        );
      } else {
        setSuggestions([]);
        setIsTextBoxDisabled(false);
      }
    })
    .catch((error) => {
      console.error("Error fetching provider names:", error);
    });
};

const fetchSearchProviders = () => {
  if (!selectedProviderData) {
    setSearchPatientData([]);
    setIsSuggestionBoxDisabled(true);
    return;
  }

  for (const rowIndex of providerAllCheckbox) {
    const item = tableDetails[rowIndex];

    item.ProvderName = selectedProviderData;
  }

  var inputStruct = {
    sProviderName: selectedProviderData,
    sPracticeID: userPracticeID,
    LoggedInUserDetails: userData,
  };

  var myInput = { sInputJSON: JSON.stringify(inputStruct) };

  var myInputData = JSON.stringify(myInput);

  const response = fetch(
    APIConstants.BASE_URL + IntelliBillConstants.GetIntelliProvidersNames,
    {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },

      body: myInputData,
    },
  )
    .then((response) => response.json())
    .then((data) => {
      const parsedData = JSON.parse(data.d);

      const providersData = parsedData.Data;

      if (Array.isArray(providersData)) {
        setSuggestionProviders(
          providersData.map((item) => ({
            label: item.split("$")[0].split("|")[1].trim(),
            val: item.split("$")[1].trim(),
          })),
        );
      } else {
        setSuggestionProviders([]);
      }
    })
    .catch((error) => {
      console.error("Error fetching provider names:", error);
    });
};

useEffect(() => {
  fetchSearchProviders(selectedProviderData);
}, [selectedProviderData]);

const handleProviderSuggestion = (suggestion) => {
  setSearchProvidersText(suggestion.label);

  setselectedProviderData(suggestion.label);
  setSearchProvidersText(suggestion.val);
  setSuggestionProviders([]);
};

const handleProviderKeyDown = (e) => {
  if (e.key === "Backspace") {
    if (selectedProviderData === "" && suggestionProviders.length > 0) {
      setSuggestionProviders([]);
    } else {
      fetchSearchProviders(selectedProviderData.slice(0, -1));
    }
  }
};

const fetchInsuranceCompanies = () => {
  var inputJSON = {
    SearchKey: searchText,
  };

  var sJSONInput = JSON.stringify(inputJSON);
  const url = `${APIConstants.BASE_URL_SEARCH}${CommonAPIConstants.GetInsuCompanyNames}?sJSONInput=${sJSONInput}`;

  fetch(url, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  })
    .then((response) => {
      if (response.status != 200) {
        console.log("response.status", response.status);
      } else return response.json();
    })
    .then((data) => {
      const parsedData = JSON.parse(data);

      const InsuranceData = parsedData.Data;

      if (Array.isArray(InsuranceData)) {
        const filteredSuggestions = InsuranceData.map((item) => ({
          label: item.Insurance,
          val: item.InsuID,
        })).filter(
          (item) => item.label !== selectedData && item.label !== "",
        );

        setSuggestions(filteredSuggestions);
      } else {
        setSuggestions([]);
        setIsTextBoxDisabled(false);
      }

      setIsTextBoxDisabled(false);
    })

    .catch((error) => {
      console.error("Error fetching Insurance names:", error);
    });
};





const handleSelectBillingNPI = (event) => {
  const selectedValue = event.target.value;
  setSelectedBillingNPI(selectedValue);
};

const filteredProviders = practiceProvider1?.filter(
  (provider) => provider.Supervisor === "1" || provider.Supervisor === "3",
);

const handleSelectFilterProviders = (event) => {
  const selectedValue = event.target.value;
  setSelectedFilterProvider(selectedValue);
};

const [selectedProvider, setSelectedProvider] = useState("");
const [tdata, setTdata] = useState("");

const handleCheckboxChange = (e) => {
  const value = e.target.checked ? 1 : 0;
  setIsChecked(value);
};

useEffect(() => {
  setSelectedCheckedRadioButton("0");
  setSelectedStatusRadioButton("0");
}, []);


const handleSuggestionChange = (e) => {
  setSelectedSearchFilter(e.target.value);
  const selectedValue = e.target.value;
  setsFilterType(selectedValue);
};




const handleInputChange = (e) => {
  const inputText = e.target.value;
  setSearchText(inputText);

  if (inputText.length > 0) {
    fetchInsuranceCompanies(inputText);
  } else {
    setSuggestions([]);
  }
};



const handleInsSuggestionList = (suggestion) => {
  setSearchText(`${suggestion.label}`);
  setselectedInsValue(`${suggestion.val}`);
  setSuggestions([]);
  setSelectedSuggestion(suggestion.label);
};


const handleInsListKeyDown = (e) => {
  if (e.key === 'Backspace') {
    if (searchText === '') {
      setSuggestions([]);


    } else {
      const newSearchText = searchText.slice(0, -1);
      fetchInsuranceCompanies(newSearchText);

    }
  }
}

const processTableData = () => {
  tableDetails.forEach((item) => {
    setRowsValidation(item.DataValidationMessage);
  });
};


const handleTableDataClick = () => {
  setLoading(true);


  var inputStruct = {

    iPracticeID: userPracticeID,
    sFromDate: startDate,
    sToDate: endDate,
    bIsCheckedIn: isChecked,
    sInsuranceID: selectedInsValue,
    sLocationID: selectedLocations2,


  };


  var objClaim = {
    'ObjClaims': inputStruct
  }


  var myInput = JSON.stringify(objClaim);
  console.log("input of" + LabBillingConstants.GetLabBillingTable, myInput);



  const url = `${APIConstants.BASE_URL_BILLING}${LabBillingConstants.GetLabBillingTable}?sInputJSON=${myInput}`;

  console.log("URL: ", url);

  fetch(url, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    }
  })

    .then((response) => {
      if (response.status != 200) { console.log("response.status", response.status); }
      else return response.json();
    })


    .then((data) => {

      console.log("tableData of" + LabBillingConstants.GetLabBillingTable, data);

      setTableDetails(data.OrderDetails);

      setTimeout(() => {
        setLoading(false);
        setFilteredTableDetails(data.OrderDetails);
      }, 10);


      const tableInsData = data.PatInsDetails


      console.log("tableInsData" + LabBillingConstants.GetLabBillingTable, tableInsData);


      setTableInsDetails(tableInsData);



    })

    .catch((error) => {
      setError('API request failed');
      setLoading(false);
    })



};
const handleDataKeyDown = (e) => {
  if (e.key === "Enter") {
    handleTableDataClick("");
  }
};

// Load the default week automatically. The grid used to open empty and sit on
// its loader until someone pressed Search; now that the page opens on a real
// range there is a query worth running straight away. Guarded by a ref so it
// fires exactly once - practiceID can settle across more than one render, and
// a second call would fire a duplicate request.
const hasAutoLoaded = useRef(false);
useEffect(() => {
  if (hasAutoLoaded.current || !userPracticeID) return;
  hasAutoLoaded.current = true;
  handleTableDataClick();
  // Intentionally keyed on the practice id alone: handleTableDataClick is
  // rebuilt every render, so listing it would re-run this on each one. The ref
  // above keeps it to a single call either way.
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [userPracticeID]);

useEffect(() => {
  window.addEventListener("keydown", handleDataKeyDown);

  return () => {
    window.removeEventListener("keydown", handleDataKeyDown);
  };
}, [startDate, endDate, sFilterType, searchText]);

useEffect(() => {
  const filteredData = filteredTableDetails.filter((item) => {
    if (
      selectedCheckedRadioButton === "All" ||
      selectedCheckedRadioButton === "CheckedIn" ||
      selectedCheckedRadioButton === "Non-CheckedIn"
    ) {
      if (
        selectedStatusRadioButton === "All" ||
        (selectedStatusRadioButton === "Submitted" &&
          item.ClaimStatus === "Submitted") ||
        (selectedStatusRadioButton === "Non-Submitted" &&
          item.ClaimStatus !== "Submitted")
      ) {
        return true;
      }
    }
    return false;
  });

  setFilteredTableDetails(filteredData);
}, [selectedCheckedRadioButton, selectedStatusRadioButton]);

const filterLocationData = (data, location, searchText) => {
  return data.filter((item) => {
    if (location === "all") {
      return true;
    } else {
      const locationToCompare = item.LocationNickname;

      return (
        locationToCompare === location ||
        (location === "Test Practice" && locationToCompare === "TP1") ||
        (location === "OH Secondary Center" && locationToCompare === "Sec")
      );
    }
  });
};



const handleCloseModals = () => {
  setShowSelectErrorModal(false);
  setShowConfirmSaveModal(false);
  setShowSuccessModal(false);
  setShowDeleteModal(false);
  setShowApprovalSaveModal(false);
  setCloseClaimsModal(false);
  setShowFaxModal(false);
  setShowICDErrorModal(false);
  setICDConfirmSaveModal(false);
  setIsEPINVerifiedModal(false);
  setErrorMessage(false);
  setShowCopaySuccessModal(false);
  setShowDownLoadErrorModal(false);
};




const handleRowClick = (index) => {
  setActiveRow(index);
};


const handlePrint = () => {
  if (!tableRef.current) {
    window.print();
    return;
  }
  const tableHTML = tableRef.current.outerHTML;
  const printWindow = window.open("", "_blank");
  if (!printWindow) return;
  printWindow.document.writeln(`
      <html>
        <head>
          <title>Lab Billing</title>
          <style>
            body { font-family: 'Segoe UI', Arial, sans-serif; margin: 14px; color: #000; }
            h1 { text-align: center; color: #1a9b8c; font-size: 18px; margin-bottom: 12px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #c0d8d4; padding: 4px 6px; font-size: 10px; text-align: left; vertical-align: top; }
            th { background: #e6f6f4; color: #0e6b60; font-weight: 700; text-transform: uppercase; letter-spacing: .3px; }
            tr:nth-child(even) td { background: #f7fbfa; }
            .enc-th-search, .enc-th-check, .enc-th-check-btn, .enc-th-trash, button { display: none !important; }
            input[type="checkbox"], select { display: none !important; }
            .enc-link { color: #1a9b8c; text-decoration: none; }
            .svg-inline--fa,
            .enc-link svg,
            .enc-tab-icon,
            .enc-table td svg,
            .enc-table th svg {
              width: 12px !important;
              height: 12px !important;
              font-size: 12px !important;
              line-height: 1 !important;
              vertical-align: middle !important;
            }
            @media print {
              @page { size: A3 landscape; margin: 10mm; }
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <h1>Lab Billing — ${(startDate)} to ${(endDate)}</h1>
          ${tableHTML}
          <script>
            window.onload = function() { window.print(); };
            window.onafterprint = function() { window.close(); };
          </script>
        </body>
      </html>
    `);
  printWindow.document.close();
};

const tableRef = useRef("");
const getClearingHouseName = (id) => {
  const found = selectClearinghouse.find((n) => n.MCHID == id);
  return found ? found.ClearingHouseName : "";
};


const getCptStatusText = (status) => {
  if (!status) return "N";

  const val = status.split("$")[1] || "";
  return val ? removeHtmlTags(val) : "";
};

const exportToExcel = () => {
  const data = filteredTableDetails.map((item, index) => {
    const id = item.IntelliLabDetailsID;
    const insId = selectedInsurances[id];
    const insurance = tableInsDetails.find(
      ins => ins.PrimaryInsID == insId
    );
    const insuranceName = insurance?.InsCompNickName || "";
    const policyNumber = policyNumbers[id] || "";
    const omitBox32Value =
      omitboxRows?.[id] == 1 ? "Yes" : "No";
    const cpt1Status = getCptStatusText(item.ScrClaimStatus);
    const cpt2Status = getCptStatusText(item.CNFClaimStatus);
    return {
      "#": index + 1,
      Location: item.LocationNickname || "",
      DOS: getDosText(item),
      "Patient Name": `${item.LastName || ""}, ${item.FirstName || ""}`,
      DOB: formatDOSDate(item.DOB) || "",
      "Insurance": insuranceName || "",
      "Policy Number": policyNumber || "",
      "Order Code": item.OrderCodes || "",
      "CPT Code": item.CPTCode1
        ? `${item.CPTCode1}${cpt1Status ? "\n" + cpt1Status : ""}`
        : "",
      "CPT Code\u200B": item.CPTCode2
        ? `${item.CPTCode2}${cpt2Status ? "\n" + cpt2Status : ""}`
        : "",
      "ICD Code": item.ICDCodes || "",
      "Case ID": item.RequisitionNumber || "",
      "Omit Box 32": omitBox32Value,
      "Rendering Provider": item.ProviderName || "",
      Requisition: item.IsOrderSubmitted === 1 ? "Submitted" : "Pending",
      Result: item.ResultPDFName ? "Available" : "",
    };
  });
  exportExcel(data, "LabBilling", "LabBilling");
};





const exportToPDF = () => {
  const pagename = 'LabBilling';


  setTimeout(() => {
    const table = tableRef.current.parentNode;
    let sPrintableData = table ? table.innerHTML : '';

    const vRegEx = new RegExp("gvClosedClaims", "");
    sPrintableData = sPrintableData.replace(vRegEx, "gvAcceptdClaims");



    const PrintableDataDetails = {
      PrintableData: sPrintableData,
      PageName: pagename,
    };

    const InputJSONLevelOne = JSON.stringify(PrintableDataDetails);
    const InputJSON = JSON.stringify(InputJSONLevelOne);



    fetch(APIConstants.SERVICES_URL + FilesAPIConstants.ExportPdfFile, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: InputJSON,
    })
      .then(response => response.json())
      .then(data => {


        if (data && data[2]) {

          const url = data[2];
          window.open(url, '_blank');


        }
      })
      .catch(err => console.error('Export to PDF failed', err))

  }, 100);
};


const handleDosLinkClick = (item) => {
  const claimId = item.CNFClaimID;

  localStorage.setItem("selectedClaimItem", JSON.stringify(item));

  const url = `/#/transactionlogs/${claimId}`;
  window.open(url, "_blank");
};

const handlePatLinkClick = (item) => {
  const url = `/#/patientdashboard/${item.PatientID}/primary-insurance`;

  localStorage.setItem("PatientID", item.PatientID);

  window.open(url, "_blank");
};


const handleSort = (key, accessor) => {
  let direction = "asc";
  if (sortConfig.key === key && sortConfig.direction === "asc") {
    direction = "desc";
  }

  const sortedData = [...filteredTableDetails].sort((a, b) => {
    let aValue = accessor ? accessor(a) : a[key];
    let bValue = accessor ? accessor(b) : b[key];

    if (aValue == null) aValue = 0;
    if (bValue == null) bValue = 0;

    if (
      key.toLowerCase().includes("date") ||
      key.toLowerCase().includes("dob") ||
      key.toLowerCase().includes("dos")
    ) {
      aValue = new Date(aValue);
      bValue = new Date(bValue);
    } else if (!isNaN(aValue) && !isNaN(bValue)) {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    } else if (typeof aValue === "string") {
      aValue = aValue.toUpperCase();
      bValue = typeof bValue === "string" ? bValue.toUpperCase() : "";
    }

    if (aValue < bValue) return direction === "asc" ? -1 : 1;
    if (aValue > bValue) return direction === "asc" ? 1 : -1;
    return 0;
  });

  setSortConfig({ key, direction });
  setFilteredTableDetails(addSelectedInsuranceName(sortedData));
};



useEffect(() => {
  const filteredData = tableDetails.filter(item => {
    const toLower = val => (typeof val === 'string' ? val.toLowerCase() : '');

    const locationMatches = searchByLocation
      ? (item.LocationNickname?.toLowerCase() || '').includes(searchByLocation.toLowerCase())
      : true;


    const appointmentDateMatches = searchByDOS
      ? getDosText(item).toLowerCase().includes(searchByDOS.toLowerCase())
      : true;

    const patientMatches = searchByPatient
      ? (
        (item.FirstName?.toLowerCase() || '').includes(searchByPatient.toLowerCase()) ||
        (item.LastName?.toLowerCase() || '').includes(searchByPatient.toLowerCase())
      )
      : true;


    const dobMatches = searchByDOB
      ? item.DOB?.toLowerCase().includes(searchByDOB.toLowerCase())
      : true;


    const providerMatches = searchByProvider
      ? item.ProviderName?.toLowerCase().includes(searchByProvider.toLowerCase())
      : true;


    const cpt1Status = item.CPTCode1
      ? `${item.CPTCode1} ${item.ScrClaimStatus?.split('$')[1] || 'N'}`
      : '';

    const cptMatches = searchByCPT
      ? toLower(cpt1Status).includes(searchByCPT.toLowerCase())
      : true;


    const cpt2Status = item.CPTCode2
      ? `${item.CPTCode2} ${item.CNFClaimStatus?.split('$')[1] || 'N'}`
      : '';

    const cpt2Matches = searchByCPT2
      ? toLower(cpt2Status).includes(searchByCPT2.toLowerCase())
      : true;


    const codesMatches = searchByCodes
      ? item.OrderCodes?.toLowerCase().includes(searchByCodes.toLowerCase())
      : true;

    const icdMatches = searchByICD1
      ? item.ICDCodes?.toLowerCase().includes(searchByICD1.toLowerCase())
      : true;

    const caseIDMatches = searchByCaseID
      ? item.RequisitionNumber?.toLowerCase().includes(searchByCaseID.toLowerCase())
      : true;

    return (
      locationMatches &&
      appointmentDateMatches &&
      patientMatches &&
      dobMatches &&
      cpt2Matches &&
      providerMatches &&
      cptMatches &&
      codesMatches &&
      icdMatches &&
      caseIDMatches
    );
  });

  setFilteredTableDetails(filteredData);

}, [searchByLocation, searchByDOS, searchByPatient, searchByDOB, searchByProvider, searchByCPT, searchByCPT2, searchByCodes,
  searchByICD1, searchByCaseID, tableDetails]);



useEffect(() => {
  let totalUnit = 0;
  let totalCharge = 0;

  filteredTableDetails.forEach((item) => {
    totalUnit += Number(item.UnitCharge || 0);
    totalCharge += Number(item.TotalCharge || 0);
  });
}, [filteredTableDetails]);




useEffect(() => {
  const initializeInsuranceData = () => {
    let updatedFilteredTableDetails = [...tableDetails];
    if (searchByPolicy.trim() !== '') {
      const policySearchLower = searchByPolicy.trim().toLowerCase();
      const matchingPatientIDs = tableInsDetails
        .filter(ins => (ins.PolicyNumber || '').toLowerCase().startsWith(policySearchLower))
        .map(ins => ins.PatientID);

      updatedFilteredTableDetails = updatedFilteredTableDetails.filter(item =>
        matchingPatientIDs.includes(item.PatientID)
      );
    }
    if (searchByInsuranceID.trim() !== '') {
      const insuranceSearchLower = searchByInsuranceID.trim().toLowerCase();
      updatedFilteredTableDetails = updatedFilteredTableDetails.filter(item => {
        const matchingInsurance = tableInsDetails.find(ins =>
          ins.PatientID === item.PatientID &&
          new Date(item.Appointment_Date) >= new Date(ins.InsStartDate) &&
          new Date(item.Appointment_Date) <= new Date(ins.InsEndDate)
        );
        return (
          matchingInsurance &&
          (matchingInsurance?.InsCompNickName || '').toLowerCase().startsWith(insuranceSearchLower)
        );
      });
    }
    const initialSelectedInsurances = {};
    const initialPolicyNumbers = {};
    updatedFilteredTableDetails.forEach((item) => {
      const defaultInsurance = tableInsDetails.find(ins =>
        ins.PatientID === item.PatientID &&
        new Date(item.Appointment_Date) >= new Date(ins.InsStartDate) &&
        new Date(item.Appointment_Date) <= new Date(ins.InsEndDate)
      );
      const id = item.IntelliLabDetailsID;
      if (defaultInsurance && defaultInsurance.PrimaryInsID) {
        initialSelectedInsurances[id] = defaultInsurance.PrimaryInsID;
        initialPolicyNumbers[id] = defaultInsurance.PolicyNumber ?? '';
      } else {
        initialSelectedInsurances[id] = '';
        initialPolicyNumbers[id] = '';
      }
    });

    setSelectedInsurances(initialSelectedInsurances);
    setPolicyNumbers(initialPolicyNumbers);
    setFilteredTableDetails(updatedFilteredTableDetails);
  };

  initializeInsuranceData();
}, [tableDetails, tableInsDetails, searchByInsuranceID, searchByPolicy]);





const handleInsuranceChange = (id, selectedPrimaryInsID) => {
  setSelectedInsurances(prev => ({
    ...prev,
    [id]: selectedPrimaryInsID
  }));
  const item = filteredTableDetails.find(x => x.IntelliLabDetailsID === Number(id));
  if (!item) {
    setPolicyNumbers(prev => ({ ...prev, [id]: '' }));
    return;
  }
  const matchingIns = tableInsDetails.find(ins =>
    ins.PatientID === item.PatientID &&
    ins.PrimaryInsID === selectedPrimaryInsID &&
    new Date(item.Appointment_Date) >= new Date(ins.InsStartDate) &&
    new Date(item.Appointment_Date) <= new Date(ins.InsEndDate)
  );
  setPolicyNumbers(prev => ({
    ...prev,
    [id]: matchingIns?.PolicyNumber ?? ''
  }));
};


const inputRef = useRef(null);
const suggestionsRef = useRef(null);

const handleClickOutside = (event) => {
  if (
    inputRef.current &&
    !inputRef.current.contains(event.target) &&
    suggestionsRef.current &&
    !suggestionsRef.current.contains(event.target)
  ) {
    setSuggestions([]);
  }
};

useEffect(() => {
  if (suggestions.length > 0) {
    document.addEventListener("mousedown", handleClickOutside);
  } else {
    document.removeEventListener("mousedown", handleClickOutside);
  }

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [suggestions]);



const addSelectedInsuranceName = (details) => {
  return details.map((item, index) => {
    const selectedInsId = selectedInsurances[index];
    const matchedIns = tableInsDetails.find(
      (ins) => ins.InsId === selectedInsId,
    );
    return {
      ...item,
      SelectedInsuranceName: matchedIns?.InsureranceCompanyname || "",
    };
  });
};

const unsetRequired = [
  selectedLocation,
  selectedBillingNPI,
  selectedCMS33a,
  selectedCMS33b,
  selectedPracticeProvider,
].filter((value) => value === "0").length;

const totalRecords = filteredTableDetails.length;
const showAllRows = pageSize === "all";
const pageCount = showAllRows
  ? 1
  : Math.max(1, Math.ceil(totalRecords / pageSize));
const safePage = Math.min(page, pageCount);
const pageStart = showAllRows ? 0 : (safePage - 1) * pageSize;
const pageEnd = showAllRows ? totalRecords : pageStart + pageSize;
const pagedTableDetails = filteredTableDetails.slice(pageStart, pageEnd);
// One lookup table per render, so each cell is a Map hit instead of a linear
// scan over every column definition.
const columnByKey = new Map(columns.map((c) => [c.key, c]));
const rangeFrom = totalRecords === 0 ? 0 : pageStart + 1;
const rangeTo = Math.min(pageEnd, totalRecords);

const pageNumbers = (() => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }
  const out = [1];
  const lo = Math.max(2, safePage - 1);
  const hi = Math.min(pageCount - 1, safePage + 1);
  if (lo > 2) out.push("gap-start");
  for (let n = lo; n <= hi; n++) out.push(n);
  if (hi < pageCount - 1) out.push("gap-end");
  out.push(pageCount);
  return out;
})();

const pinClassFor = (key) => {
  const at = CONTEXT_KEYS.indexOf(key);
  return at === -1 ? "" : `hv-pin hv-pin--${at + 1}`;
};


const handlePageSizeChange = (e) => {
  const value = e.target.value;
  setPageSize(value === "all" ? "all" : Number(value));
  setPage(1);
};

const handleChangeModifier = (e) => {
  const value = e.target.value;
  setselectedModifier(value);
};



const locationRef = useRef(null);
const locationNPIRef = useRef(null);
const providerNPIRef = useRef(null);
const taxonomyRef = useRef(null);



const getRowByID = (id) => {
  return filteredTableDetails.find(
    (item) => item.IntelliLabDetailsID === Number(id)
  );
};
const getFilteredInsDetailsForItem = (item) => {
  if (!item || !tableInsDetails) return [];

  const apptDate = new Date(item.Appointment_Date);

  return tableInsDetails.filter(ins =>
    ins.PatientID === item.PatientID &&
    new Date(ins.InsStartDate) <= apptDate &&
    new Date(ins.InsEndDate) >= apptDate
  );
};


const GenerateNewLabClaims = async (isConfirmed = false) => {
  let hasError = false;
  let firstErrorFocused = false;
  if (!selectedBillingNPI || selectedBillingNPI <= 0) {
    setLocationNPIError('Select location NPI.');
    hasError = true;
    if (!firstErrorFocused) {
      locationNPIRef.current?.focus();
      firstErrorFocused = true;
    }
  } else {
    setLocationNPIError('');
  }

  if (!selectedCMS33a || selectedCMS33a <= 0) {
    setProviderNPIError('Select provider NPI.');
    hasError = true;
    if (!firstErrorFocused) {
      providerNPIRef.current?.focus();
      firstErrorFocused = true;
    }
  } else {
    setProviderNPIError('');
  }

  if (selectedCMS33b === '0') {
    setTaxonomyCodeError('Select taxonomy code.');
    hasError = true;
    if (!firstErrorFocused) {
      taxonomyRef.current?.focus();
      firstErrorFocused = true;
    }
  } else {
    setTaxonomyCodeError('');
  }

  if (isLocChecked) {
    if (!selectedLocation || selectedLocation <= 0) {
      setLocationError("Please select a Place of Service.");
      hasError = true;
      if (!firstErrorFocused) {
        locationRef.current?.focus();
        firstErrorFocused = true;
      }
    } else {
      setLocationError("");
    }
  } else {
    setLocationError("");
  }

  if (hasError) {
    setShowSelectErrorModal(false);
    return;
  }

  const anySelected = Object.values(rowSelections).some((r) => r.selected);

  if (!anySelected) {
    setShowSelectErrorModal(true);
    setShowSuccessModal(false);
    return;
  }

  if (!isConfirmed) {
    setShowApprovalSaveModal(true);
    return;
  }

  setShowSelectErrorModal(false);
  setShowApprovalSaveModal(false);
  setLoading(true);

  let ClaimArray = [];

  for (const id in rowSelections) {
    const sel = rowSelections[id];
    if (!sel.selected) continue;

    const item = getRowByID(id);
    if (!item) continue;

    const ICDCode = item.ICDCodes || "";
    let ClaimLineItems = [];

    if (sel.cpt1) {
      ClaimLineItems.push({
        CPTcode: item.CPTCode1 || "",
        Modifier: selectedModifier,
        ICD1: ICDCode,
        CPTServiceID: 1,
      });
    }

    if (sel.cpt2) {
      ClaimLineItems.push({
        CPTcode: item.CPTCode2 || "",
        Modifier: selectedModifier,
        ICD1: ICDCode,
        CPTServiceID: 1,
      });
    }

    const claimDetails = {
      PracticeID: userPracticeID,
      PatientID: item.PatientID,
      DOS: formatDOSDate(item.Appointment_Date),
      ProviderID: item.ProviderID || 0,
      SupervisorIndicator: selectedProvidersCategory,
      SupervisorProviderID: selectedFilterProvider,
      SkipBox32: omitboxRows[id] ?? 0,
      Insurance: selectedInsurances[id],
      PolicyNumber: policyNumbers[id],
      Claim24B: selectedServiceLocation,
      SchedulingProviderID: item.ProviderID || 0,
      Claim32A: selectedBillingNPI,
      Claim33A: selectedCMS33a,
      Claim33B: selectedCMS33b,
      PlaceofService: isLocChecked
        ? selectedLocation
        : item.MasterPatientLocationID || "",
      UserID: userData.userID,
      RoleID: userData.roleID,
      OrderType: item.OrderType,
      IsClaimSplit: 0,
      IsScheduleAppointment: 0,
      PageModule: "Labbilling",
      PatientSecInsurance: 0,
      PatientSecInsurancePolicy: 0,
      ClaimENote: 0,
      IsPrimarySkipped: 0,
      objCPTDetails: ClaimLineItems,
    };

    ClaimArray.push(claimDetails);
  }

  const inputJson = JSON.stringify(JSON.stringify({ objClaim: ClaimArray }));

  try {
    const response = await fetch(
      APIConstants.BASE_URL_BILLING + LabBillingConstants.GenerateLabClaims,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${sAuthToken}`,
        },
        body: inputJson,
      }
    );

    const responseData = await response.json();

    if (responseData > 0) {
      setTimeout(() => {
        setLoading(false);
        setUpdatedTableData(responseData);
        handleTableDataClick();
      }, 10);

      setShowSuccessModal(true);
      setShowSelectErrorModal(false);
      setSavedRowCount(
        Object.values(rowSelections).filter((r) => r.selected).length
      );
      setRowSelections({});
    } else {
      console.error("Error sending data to the API");
      setLoading(false);
    }
  } catch (error) {
    console.error("An error occurred:", error);
    setLoading(false);
  }
};



const GetDownloadFile = async () => {
  setShowDownLoadErrorModal(false);

  const anySelected = Object.values(rowSelections).some((r) => r.selected);

  if (!anySelected) {
    setShowDownLoadErrorModal(true);
    return;
  }

  const FileArray = [];

  for (const id in rowSelections) {
    if (!rowSelections[id].selected) continue;

    const item = getRowByID(id);
    if (!item) continue;

    FileArray.push({
      FileName: item.ReqPDFName || "",
      FilePhyPath: "",
      FileType: item.FileType,
      PatientID: item.PatientID,
    });
  }

  const dataToSend = {
    FileArray,
    PatientName: "Lab REQ",
    PracticeID: userPracticeID,
  };

  const inputJson = JSON.stringify(JSON.stringify(dataToSend));

  try {
    const response = await fetch(
      APIConstants.SERVICES_URL + LabBillingConstants.GenerateDownloadFile,
      {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: inputJson,
      }
    );

    const data = await response.json();

    if (response.ok) {
      setShowDownLoadErrorModal(false);
      window.open(data, "_blank");
    }
  } catch (error) {
    console.error("Download error:", error);
  }
};


const handlePracticeButtonClick = (item) => {
  setSelectedCMS33a(item.NPI);

  setSelectedBillingNPI(item.NPI);

  const matchedTaxonomy = practiceTaxonomycodes.find(
    (tax) => tax.PracticeLabel === item.PracticeLabel
  );

  if (matchedTaxonomy) {
    setSelectedCMS33b(matchedTaxonomy.Taxonomycode);

  }
};


const handleLocCheckboxChange = (e) => {
  setIsLocChecked(e.target.checked);
};



return (
  <>
    <div className="ib2-app">
      {}
      <style>{labBillingStyles}</style>
      {}
      <div className="hv-container hv-page hv-page--dense hv-page--fill">
        <div className="hv-pagehead">
          <div>
            <h1 className="hv-pagehead__title">Lab Billing</h1>
            <p className="hv-pagehead__sub">
              Buckeye Lab · order-based claims for the selected date range
            </p>
          </div>
          <div className="hv-pagehead__actions">
            <span className="hv-pill hv-pill--neutral">
              {filteredTableDetails.length} records
            </span>
          </div>
        </div>

        <div className="hv-toolbar">
          <div className="hv-toolbar__group">
            <label className="hv-toolbar__label">From:</label>
            <DatePicker
              selected={startDate ? new Date(startDate) : null}
              onChange={handleStartDateChange}
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              readOnly={isHideChecked}
              className={`hv-date ${startDate ? "default-selected" : ""}`}
              placeholderText="MM/dd/yyyy"
              calendarContainer={CalendarStartContainer}
              popperClassName="ib2-dp"
              calendarClassName="ib2-dp-cal"
              open={isStartOpen}
              onFocus={() => setIsStartOpen(true)}
              onInputClick={() => setIsStartOpen(true)}
              onClickOutside={() => setIsStartOpen(false)}
              renderCustomHeader={({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => {
                const selectedDate =
                  date instanceof Date && !isNaN(date) ? date : new Date();
                const selectedMonth = selectedDate.getMonth();
                const selectedYear = selectedDate.getFullYear();

                return (
                  <div>
                    <button
                      className="hv-btn hv-btn--sm hv-btn--quiet"
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled}
                    >
                      {"<"}
                    </button>
                    <select
                      className="hv-select hv-select--sm"
                      value={selectedMonth}
                      onChange={({ target: { value } }) =>
                        changeMonth(Number(value))
                      }
                    >
                      {getMonths(selectedYear)}
                    </select>
                    <select
                      className="hv-select hv-select--sm"
                      value={selectedYear}
                      onChange={({ target: { value } }) =>
                        changeYear(Number(value))
                      }
                    >
                      {Array.from({ length: 100 }, (v, k) => (
                        <option
                          key={k}
                          value={new Date().getFullYear() - k}
                        >
                          {new Date().getFullYear() - k}
                        </option>
                      ))}
                    </select>
                    <button
                      className="hv-btn hv-btn--sm hv-btn--quiet"
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled}
                    >
                      {">"}
                    </button>
                  </div>
                );
              }}
            />

            <label className="hv-toolbar__label">To: </label>
            <DatePicker
              selected={endDate ? new Date(endDate) : null}
              onChange={handleEndDateChange}
              dateFormat="MM/dd/yyyy"
              maxDate={new Date()}
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={100}
              className={`hv-date ${endDate ? "default-selected" : ""}`}
              placeholderText="MM/dd/yyyy"
              popperPlacement="bottom-start"
              readOnly={isHideChecked}
              calendarContainer={CalendarContainer}
              popperClassName="ib2-dp"
              calendarClassName="ib2-dp-cal"
              open={isEndOpen}
              onFocus={() => setIsEndOpen(true)}
              onInputClick={() => setIsEndOpen(true)}
              onClickOutside={() => setIsEndOpen(false)}
              renderCustomHeader={({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => {
                const selectedDate =
                  date instanceof Date && !isNaN(date) ? date : new Date();
                const selectedMonth = selectedDate.getMonth();
                const selectedYear = selectedDate.getFullYear();

                return (
                  <div>
                    <button
                      className="hv-btn hv-btn--sm hv-btn--quiet"
                      onClick={decreaseMonth}
                      disabled={prevMonthButtonDisabled}
                    >
                      {"<"}
                    </button>
                    <select
                      className="hv-select hv-select--sm"
                      value={selectedMonth}
                      onChange={({ target: { value } }) =>
                        changeMonth(Number(value))
                      }
                    >
                      {getMonths(selectedYear)}
                    </select>
                    <select
                      className="hv-select hv-select--sm"
                      value={selectedYear}
                      onChange={({ target: { value } }) =>
                        changeYear(Number(value))
                      }
                    >
                      {Array.from({ length: 100 }, (v, k) => (
                        <option
                          key={k}
                          value={new Date().getFullYear() - k}
                        >
                          {new Date().getFullYear() - k}
                        </option>
                      ))}
                    </select>
                    <button
                      className="hv-btn hv-btn--sm hv-btn--quiet"
                      onClick={increaseMonth}
                      disabled={nextMonthButtonDisabled}
                    >
                      {">"}
                    </button>
                  </div>
                );
              }}
            />
          </div>


          <select
            name="select"
            className="hv-select"
            value={selectedSearchFilter}
            onChange={handleSuggestionChange}
          >

            {labDropDownData.map((item, index) => (

              <option key={index} value={item.LabID}>{item.LabName}</option>
            ))}
          </select>


          <div className="hv-field hv-field--search">

            <span className="hv-field__label">Insurance:</span>

            <input
              type='text'
              value={searchText}
              onChange={handleInputChange}
              onKeyDown={handleInsListKeyDown}
              className='hv-input'
            />
            <div className='autocomplete'>
              <ul className='list-style'>
                {suggestions.map((suggestion) => (
                  <li key={suggestion.val} onClick={() => handleInsSuggestionList(suggestion)}>{suggestion.label}</li>
                ))}
              </ul>
            </div>

          </div>


          <select
            name="select"
            className="hv-select"
            style={{
              width: '150px', height: '30px', marginTop: '3px',
              backgroundColor:
                selectedLocations2 !== '-1'
                  ? (locations.find((loc) => loc.LocationId == selectedLocations2)?.Color || '#fff')
                  : '#fff'
            }}

            value={selectedLocations2} onChange={(e) => setselectedLocations2(e.target.value)}

          >
            <option value="-1">All</option>

            {locations.map((item, index) => (

              <option key={index} value={item.LocationId} style={{ backgroundColor: item.Color }}>{item.LocationName}</option>
            ))}
          </select>

          <label className="hv-check">
            <input
              type="checkbox"
              checked={isChecked}
              onChange={handleCloseClaimCheckbox}
            />
            Checked-in only
          </label>

          <button
            type="button"
            className="hv-btn"
            onClick={(e) => handleTableDataClick(e)}
          >
            Search
          </button>


          <span className="hv-exports">
            <button
              type="button"
              className="hv-icon-btn hv-icon-btn--sm hv-tip"
              data-tip="Print"
              onClick={handlePrint}
            >
              <FontAwesomeIcon icon={faPrint} />
            </button>
            <button
              type="button"
              className="hv-icon-btn hv-icon-btn--sm hv-tip hv-t-green"
              data-tip="Excel"
              onClick={exportToExcel}
            >
              <FontAwesomeIcon icon={faFileExcel} />
            </button>
            <button
              type="button"
              className="hv-icon-btn hv-icon-btn--sm hv-tip hv-t-red"
              data-tip="PDF"
              onClick={exportToPDF}
            >
              <FontAwesomeIcon icon={faFilePdf} />
            </button>
          </span>

        </div>

        <div className={`hv-defaults${defaultsOpen ? " is-open" : ""}`}>
          <button
            type="button"
            className="hv-defaults__head"
            onClick={() => setDefaultsOpen((o) => !o)}
            aria-expanded={defaultsOpen}
          >
            <span className="hv-defaults__title">Claim defaults</span>
            {unsetRequired > 0 && (
              <span className="hv-defaults__flag">
                {unsetRequired} required not set
              </span>
            )}
            <span className="hv-defaults__summary">
              Applied to every claim in this batch
            </span>
            <span className="hv-defaults__caret">▾</span>
          </button>
          <div className="hv-defaults__body">
            <div className="hv-defaults__grid">

              <div className="hv-field">
                <span className="hv-field__label">
                  Referring provider (17)
                </span>
                <div className="hv-row hv-row--tight">
                  <select
                    name="select"
                    id=""
                    className="hv-select"
                    value={selectedProvidersCategory}
                    onChange={(e) =>
                      setSelectedProvidersCategory(e.target.value)
                    }
                  >
                    {providerCategory.map((item, index) => (
                      <option key={index} value={item.Value}>
                        {item.Label}
                      </option>
                    ))}
                  </select>

                  <select
                    name="select"
                    className="hv-select"
                    onChange={handleSelectFilterProviders}
                    value={selectedFilterProvider}
                  >
                    <option value="">&lt;--Select--&gt;</option>

                    {filteredProviders.map((provider) => (
                      <option
                        key={provider.ProviderID}
                        value={provider.ProviderID}
                      >
                        {provider.ProviderName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>


              <div className="hv-field">
                <span className="hv-field__label">
                  Service Loc(24 B)
                </span>
                <div className="hv-row hv-row--tight">
                  <select
                    name="select"
                    id=""
                    className="hv-select"
                    value={selectedServiceLocation}
                    onChange={(e) => handleSelectChange(e)}
                  >

                    {ServiceLocations.map((item, index) => (

                      <option key={index} style={{ color: 'black' }} value={item.ID}>{item.PlaceOfService}</option>
                    ))}
                  </select>


                </div>
              </div>


              {}
              <div
                className={`hv-field ${selectedLocation === "-1" ? "is-invalid" : "is-valid"}`}
              >
                <span className="hv-field__label hv-req">
                  Service Facility (32)
                  <span style={{ marginLeft: '1px' }}>
                    <input type='checkbox'
                      checked={isLocChecked}
                      onChange={handleLocCheckboxChange}
                    />
                  </span>
                </span>

                <select
                  name="select"
                  className="hv-select"
                  ref={locationRef}
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                >
                  <option value="-1">&lt;--Select--&gt;</option>
                  {locations.map((item, index) => (
                    <option value={item.LocationId} key={index}>
                      {item.LocationName}
                    </option>
                  ))}
                </select>

                {selectedLocation <= 0 && (
                  <span className="hv-field__error">{locationError}</span>
                )}
              </div>


              <div
                className={`hv-field ${selectedBillingNPI === "0" ? "is-invalid" : "is-valid"}`}
              >
                <span className="hv-field__label hv-req">
                  Service location NPI (32 A)
                </span>
                <select
                  name="select"
                  className="hv-select"
                  value={selectedBillingNPI}
                  onChange={handleSelectBillingNPI}
                >
                  <option value="0">&lt;--Select--&gt;</option>
                  {BillingNPIs.map((item, index) => (
                    <option value={item.NPI} key={index}>
                      {item.NamewithNPI}
                    </option>
                  ))}
                </select>

                {selectedBillingNPI <= 0 && (
                  <span className="hv-field__error">{locationNPIError}</span>
                )}
              </div>

              {}


              <div
                className={`hv-field ${selectedCMS33a === "0" ? "is-invalid" : "is-valid"}`}
              >
                <span className="hv-field__label hv-req">
                  Billing provider NPI (33 A)
                </span>

                <select
                  name="select"
                  className="hv-select"
                  value={selectedCMS33a}
                  onChange={(e) => setSelectedCMS33a(e.target.value)}
                >
                  <option value="0">&lt;--Select--&gt;</option>
                  {cms33aData.map((item, index) => (
                    <option key={index} value={item.NPI}>
                      {item.NamewithNPI}
                    </option>
                  ))}
                </select>
                {selectedCMS33a <= 0 && (
                  <span className="hv-field__error">{providerNPIError}</span>
                )}
              </div>

              <div
                className={`hv-field ${selectedCMS33b === "0" ? "is-invalid" : "is-valid"}`}
              >
                <span className="hv-field__label hv-req">
                  Billing provider taxonomy (33 B)
                </span>

                <select
                  name="select"
                  id=""
                  className="hv-select"
                  value={selectedCMS33b}
                  onChange={(e) => setSelectedCMS33b(e.target.value)}
                >
                  <option value="0">&lt;--Select--&gt;</option>
                  {cms33bData.map((item, index) => (
                    <option key={index} value={item.TaxonomyCode}>
                      {item.NamewithTaxCode}
                    </option>
                  ))}
                </select>
                {selectedCMS33b == 0 && (
                  <span className="hv-field__error">{taxonomyCodeError}</span>
                )}
              </div>


              <div className="hv-field"><span className="hv-field__label">Modifier</span>
                <input className="hv-input" value={selectedModifier}
                  onChange={handleChangeModifier} />
              </div>

              <div style={{ display: "flex", marginTop: '20px' }}>
                <div style={{ display: "flex", marginLeft: '1px', marginTop: '0px', justifyContent: 'space-between', width: 'auto', gap: '5px' }}>
                  {BillingNPIs.map((item, index) => (
                    <div key={index}>
                      {item.PracticeLabel && (
                        <button
                          className="hv-btn hv-btn"
                          onClick={() =>
                            handlePracticeButtonClick(item)
                          }
                          value={`Selected ${item.PracticeLabel}`}
                        >
                          {item.PracticeLabel}
                        </button>
                      )}



                    </div>
                  ))}
                </div>



              </div>
            </div>
          </div>
        </div>

       
        {loading && (
          <div className="overlay">
            <div className="ib2-loader" role="status" aria-live="polite">
              <div className="ib2-loader-coins" aria-hidden="true">
                <span>🪙</span>
                <span>💵</span>
                <span>🧾</span>
              </div>
              <div className="ib2-loader-bar">
                <div className="ib2-loader-bar-fill" />
              </div>
              <div className="ib2-loader-title">Healthiva is on it…</div>
              <div className="ib2-loader-quip">
                {BILLING_QUIPS[quipIndex % BILLING_QUIPS.length]}
              </div>
            </div>
          </div>
        )}

    
        <div className="hv-panel hv-panel--flush">
          <div className="hv-tabbar hide-on-print" role="tablist">
            {columnTabs.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeColumnTab === tab.id}
                className={`hv-tabbar__tab ${activeColumnTab === tab.id ? "is-active" : ""}`}
                onClick={() => setActiveColumnTab(tab.id)}
                type="button"
              >
                <FontAwesomeIcon
                  className="ib2-col-tab-icon"
                  icon={tab.icon}
                  aria-hidden="true"
                />
                <span className="ib2-col-tab-label">{tab.label}</span>
              </button>
            ))}


            <div className="hv-tablebar hide-on-print margin-left-auto margin-right-10px">


              <span>
                {rangeFrom}–{rangeTo} of {totalRecords} Requisitions
              </span>

             
              <div className="hv-pagi">
                <span className="hv-pagi__rows">
                  Rows
                  <select
                    className="hv-select hv-select--sm"
                    value={pageSize}
                    onChange={handlePageSizeChange}
                  >
                    <option value={10}>10</option>
                    <option value={30}>30</option>
                    <option value={60}>60</option>
                    <option value={100}>100</option>
                    <option value="all">All</option>
                  </select>
                </span>

                <button
                  type="button"
                  className="hv-pagi__btn"
                  disabled={safePage === 1 || showAllRows}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  ‹
                </button>

                {!showAllRows &&
                  pageNumbers.map((n) =>
                    typeof n === "number" ? (
                      <button
                        key={n}
                        type="button"
                        className={`hv-pagi__btn${n === safePage ? " is-active" : ""}`}
                        onClick={() => setPage(n)}
                      >
                        {n}
                      </button>
                    ) : (
                      <span key={n} className="hv-pagi__gap">
                        …
                      </span>
                    ),
                  )}

                <button
                  type="button"
                  className="hv-pagi__btn"
                  disabled={safePage === pageCount || showAllRows}
                  onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                >
                  ›
                </button>
              </div>
            </div>

          </div>


          {}
          {}
          <div
            ref={gridScrollRef}
            className="hv-table-scroll hv-table-scroll--fade"
          >
            <table
              className="hv-table hv-worksheet hv-worksheet--pinned hv-table--striped"
              ref={tableRef}
            >
              <thead>
                <tr className="align-top">
                  {}
                  {selectedColumns.map((key) => {
                    const col = columnByKey.get(key);
                    return col ? (
                      <th
                        key={col.key}
                        className={`ib2-th-${col.key} ${col.isLink ? "is-sortable" : ""} ${pinClassFor(col.key)} ${
                          GROUP_START_KEYS.includes(col.key) ? "ib2-group-start" : ""
                          }`}
                        style={
                          pinClassFor(col.key)
                            ? undefined
                            : { minWidth: COLUMN_WIDTHS[col.key] }
                        }
                      >
                        <span className="ib2-th-title">
                          {col.isLink ? (
                            <Link onClick={() => handleSort(col.key)}>
                              {col.label}
                            </Link>
                          ) : (
                            col.label
                          )}
                          {col.headerCheckbox && (
                            <input
                              type="checkbox"
                              className="hide-on-print"
                              onChange={col.headerCheckbox.onChange}
                              aria-label={`Select all ${col.label}`}
                              title={`Select all ${col.label}`}
                            />
                          )}
                          {col.headerExtra && (
                            <span className="ib2-th-extra">{col.headerExtra}</span>
                          )}
                        </span>
                        {col.hasSearch && (
                          <div>
                            <input
                              className="hide-on-print ib2-th-search"
                              type="text"
                              placeholder="Search"
                              aria-label={`Search ${col.label}`}
                              title={`Search ${col.label}`}
                              value={col.searchValue}
                              onChange={(e) =>
                                col.setSearchValue(e.target.value)
                              }
                            />
                          </div>
                        )}
                        {col.hasCheckbox && (
                          <label
                            className="ib2-th-control hide-on-print"
                            title={col.checkboxTitle}
                          >
                            <input
                              type="checkbox"
                              checked={col.checkboxValue}
                              onChange={col.onCheckboxChange}
                              aria-label={col.checkboxTitle}
                            />
                            {col.checkboxLabel && (
                              <span className="ib2-th-control-text">
                                {col.checkboxLabel}
                              </span>
                            )}
                          </label>
                        )}
                        {col.hasButton && (
                          <div className="ib2-th-buttons">
                            {col.buttons?.map((btn, i) => (
                              <button
                                key={i}
                                className={btn.className}
                                onClick={btn.onClick}
                                title={btn.title}
                                aria-label={btn.title || btn.label}
                              >
                                {btn.label}
                              </button>
                            ))}
                          </div>
                        )}
                        {col.hasSelect && (
                          <div>
                            <select
                              className="hide-on-print ib2-th-select"
                              value={col.selectValue}
                              onChange={(e) =>
                                col.onSelectChange(e)
                              }
                            >
                              <option value="">--Select--</option>
                              {col.selectOptions?.map((opt, i) => (
                                <option key={i} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </th>
                    ) : null;
                  })}
                </tr>
              </thead>

              <tbody>

                {pagedTableDetails
                  .map((item, index) => {
                    return (
                      <Fragment key={index}>
                        <tr
                          className={`${item.iClosed == 1
                            ? "table-secondary"
                            : activeRow === index
                              ? "table-warning"
                              : ""
                            } ${
                            item.DataValidationMessage && item.iClosed != 1 ? "is-invalid" : ""
                            }`}
                          onClick={() => handleRowClick(index)}
                          data-toggle="tooltip"
                          data-placement="top"
                          title={
                            item.DataValidationMessage
                              ? item.DataValidationMessage
                              : item.isReadyforApproval === "0"
                                ? "Claim is not saved for approval"
                                : ""
                          }
                        >
                          {}
                          {selectedColumns.map((key) => {
                            const col = columnByKey.get(key);
                            return col ? (
                              <td
                                key={col.key}
                                className={`hv-td-nowrap ${pinClassFor(col.key)} ${GROUP_START_KEYS.includes(col.key)
                                  ? "ib2-group-start"
                                  : ""
                                  }`}
                              >
                                {col.render
                                  ? col.render(item, index)
                                  : item[col.key]}
                              </td>
                            ) : null;
                          })}
                        </tr>
                        {item.DataValidationMessage &&
                          item.iClosed != 1 &&
                          expandedIssueRows.includes(item.CPTRowID) && (
                            <tr className="hv-errrow">
                              <td colSpan={selectedColumns.length}>
                                <div className="hv-errlist">
                                  <FontAwesomeIcon icon={faTriangleExclamation} />
                                  {}
                                  {item.DataValidationMessage.split("\n")
                                    .map((msg) => msg.trim())
                                    .filter(Boolean)
                                    .map((msg, msgIdx) => (
                                      <span key={msgIdx}>{msg}</span>
                                    ))}
                                </div>
                              </td>
                            </tr>
                          )}
                      </Fragment>
                    );
                  })}
              </tbody>
            </table>
          </div>
          {errorMessage && (
            <div className="alertmsg-modal">
              <label style={{ backgroundColor: "#18A6EC" }}></label>
              <FontAwesomeIcon
                style={{
                  float: "right",
                  fontWeight: "bolder",
                  fontSize: "1rem",
                  backgroundColor: "red",
                  padding: "5px",
                  cursor: "pointer",
                }}
                onClick={handleCloseModals}
                icon={faXmark}
              />
              <div
                style={{
                  backgroundColor: "#18A6EC",
                  color: "white",
                  padding: "10px",
                }}
              >
                {errorMessage.split("\n").map((msg, index) => (
                  <p key={index} style={{ margin: "4px 0" }}>
                    {msg}
                  </p>
                ))}
              </div>
            </div>
          )}


          {showDownLoadErrorModal && (

            <div className='alert-modal'>

              <p className='mt-3'>Select one or more files to generate Download by selecting the check box!!</p>
              <button className='btn btn-danger' style={{ width: '50px', height: '30px', padding: '4px' }} onClick={handleCloseModals}>Ok</button>

            </div>

          )}


          {showPopup && (
            <div
              style={{
                position: "fixed",
                bottom: "50px",
                left: "50%",
                transform: "translateX(-50%)",
                backgroundColor: "#333",
                color: "#fff",
                padding: "10px 20px",
                borderRadius: "5px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
                zIndex: 1000,
                height: "100px",
                width: "400px",
                textAlign: "center",
              }}
            >
              <label
                style={{
                  marginTop: "20px",
                  fontSize: "1.4rem",
                  border: "#333",
                }}
              >
                {popupMessage}
              </label>
            </div>
          )}

          {showSuccessModal && (
            <div
              className="alertmsg-modal"
              role="alert"
              style={{
                left: "20%",
                top: "40%",
                height: "100px",
                width: "max-content",
                fontSize: "1.4rem",
              }}
            >
              {`${savedRowCount}  claim successfully Saved`}!!
              {showSuccessModal}
            </div>
          )}

          {msgSuccesmodal && (
            <div
              className="alert alert-success aleart-modal"
              role="alert"
              style={{ marginLeft: "200px", width: "1000px" }}
            >
              {`${savedRows}  claim successfully Saved`}
            </div>
          )}


          {showSelectErrorModal && (
            <div className="alert-modal">
              <p>please select atleast one claim...</p>
              <button
                className="btn btn-primary"
                onClick={handleCloseModals}
              >
                Ok
              </button>
            </div>
          )}

          {showApprovalSaveModal && (
            <div className="alert-modal">
              <p>Are you sure you want to generate the selected claims?</p>
              <button
                className="btn btn-primary"
                onClick={handleCloseModals}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                style={{ marginLeft: "40px" }}
                onClick={() => GenerateNewLabClaims(true)}
              >
                Confirm
              </button>
            </div>
          )}




        </div>




        <div className="hv-actionbar hide-on-print">
          <span className="hv-actionbar__count">
            <strong>{selectedRows.length}</strong> of{" "}
            {filteredTableDetails.length} selected
          </span>
          <span className="hv-actionbar__spacer" />

          <button
            className="hv-btn hv-btn--ghost"
            onClick={GetDownloadFile}
          >
            Download
          </button>

          <button className="hv-btn" onClick={GenerateNewLabClaims}>
            Generate Claim
          </button>
        </div>
      </div>
    </div>
  </>
);
};

export default LabBilling;